export default {
  routes: {
    meeting: {
      badge: "Free demo",
      heroTitleLine1: "Let's bring your offer",
      heroTitleLine2: "live.",
      heroDescription:
        "30 minutes, no obligation. We show you the app, dashboard and locking systems for your specific use case.",
      bullets: [
        "Live tour of the platform",
        "Feasibility & locking-system check",
        "A tailored price quote",
      ],
      trustedBy: "Trusted by",
    },
    team: {
      kicker: "About us",
      heroTitleLine1: "We make sharing",
      heroTitleLine2: "possible for everyone.",
      heroDescription:
        "JUHUU started from a simple idea: one app to find, book and use everything around you — and a platform that makes that effortless for providers.",
      missionKicker: "Our mission",
      missionTitle: "Reimagining the sharing economy.",
      missionText:
        "We integrate and empower existing providers across shared mobility, shared infrastructure and shared tourism — with drastically reduced development cost, less maintenance and faster time-to-market. We don't just take part in the sharing economy; we shape it.",
      valuesKicker: "What drives us",
      valuesTitle: "Clear vision, real solutions.",
      values: [
        {
          title: "Solve real problems",
          description:
            "We don't just talk about the future — we build it. Innovation that works in everyday life.",
        },
        {
          title: "Partnership",
          description:
            "We don't want to be a tool, we want to be a partner. More customers, less effort — that's JUHUU.",
        },
        {
          title: "Sustainability",
          description:
            "Share instead of own. We help people move more sustainably.",
        },
      ],
      awardTitle: "riz up GENIUS Award 2023",
      awardSubtitle: "1st place — category \u201Cregional genius\u201D",
      awardText:
        "Awarded for innovative solutions that solve real problems in a dynamic market.",
      teamKicker: "The team",
      teamTitle: "The people behind JUHUU.",
      members: [
        { initials: "BM", name: "Ben Müller-Niklas", role: "Founder & CEO" },
        { initials: "MW", name: "Moritz Wallner", role: "Product & Engineering" },
        { initials: "MM", name: "Marc Müller-Niklas", role: "Partnerships" },
        {
          initials: "MÜ",
          name: "Michael Übermasser",
          role: "Operations & Support",
        },
      ],
      locationKicker: "Location",
      locationTitleLine1: "From Austria,",
      locationTitleLine2: "for Europe.",
      locationText:
        "JUHUU builds and operates the platform in Austria, with data on EU servers and a focus on the European market.",
      locationCta: "Get to know us",
      locationBadge: "🇦🇹 Österreich",
      finalCtaTitle: "Join the sharing economy.",
      finalCtaDescription:
        "As an operator, manufacturer or partner — let's move things together.",
      finalCtaButton: "Get in touch",
    },
    references: {
      kicker: "References",
      heroTitleLine1: "Operators who",
      heroTitleLine2: "run on JUHUU.",
      heroDescription:
        "From the train station to the bike shop: operators of every size run their sharing and rental business with JUHUU.",
      stats: [
        { value: "20+", label: "active operators" },
        { value: "< 1", label: "months to live" },
        { value: "14+", label: "languages" },
        { value: "24/7", label: "automated operation" },
      ],
      cases: [
        {
          org: "ÖBB-Personenverkehr AG",
          sub: "Bike:Parking",
          titleLine1: "Secure bike parking",
          titleLine2: "at the station — automated.",
          description:
            "Dozens of weather-protected spots and bike boxes, booked via the app and opened by QR scan. Annual, monthly and daily tariffs are managed entirely through the JUHUU dashboard.",
          stats: [
            { value: "50+", label: "Spots" },
            { value: "24/7", label: "Access" },
            { value: "0", label: "On-site staff" },
          ],
          quote:
            "\u201COur customers book via the JUHUU app and open the box with a QR scan. We see every rental live in the dashboard — with no on-site staff at all.\u201D",
          authorRole: "Project lead",
          authorOrg: "ÖBB Bike:Parking",
          authorInitials: "Ö",
        },
        {
          org: "City of Fellbach",
          sub: "Bike tower at the station",
          titleLine1: "A software takeover",
          titleLine2: "for the bike tower.",
          description:
            "The 16 m bike tower parks up to 76 bikes fully automatically. Its control software originally came from manufacturer Koch & Partner. JUHUU took it over and now runs the whole tower: booking via app, access by QR at one of four gates, plus transit integration through the polygoCard.",
          stats: [
            { value: "76", label: "Spots" },
            { value: "16 m", label: "Tower height" },
            { value: "ÖPNV", label: "polygoCard integration" },
          ],
          quote:
            "\u201COur bike tower ran on the manufacturer's software for years. JUHUU took over the control entirely — today commuters book via the app, and transit-pass holders park for free with their polygoCard.\u201D",
          authorRole: "City of Fellbach",
          authorOrg: "Fellbach.go! radbox",
          authorInitials: "SF",
          transitLabel: "Transit integration:",
          transitText:
            "a stored polygoCard (VVS) = free parking for transit-pass holders.",
        },
        {
          org: "Stadtwerke Osnabrück",
          sub: "Central-station bike hub",
          titleLine1: "Another tower,",
          titleLine2: "centrally controlled.",
          description:
            "At Osnabrück's Altstadt station too, JUHUU controls an automated bike tower and delivers secure parking with no on-site staff. Commuters book via the app.",
          stats: [
            { value: "160", label: "Spots" },
            { value: "24/7", label: "Access" },
          ],
          quote:
            "\u201CAt the station we wanted secure bike parking without staff. With JUHUU we control the tower centrally and see every booking live.\u201D",
          authorRole: "Mobility team",
          authorOrg: "Stadtwerke Osnabrück",
          authorInitials: "OS",
        },
        {
          org: "Allgäutissimo Bikeshop",
          sub: "E-MTB rental",
          titleLine1: "E-bike rental",
          titleLine2: "that runs itself.",
          description:
            "A small bike shop renting premium e-MTBs through bike boxes. With the JUHUU dashboard the team manages rentals, tariffs and deposits. All without an in-house IT team.",
          stats: [
            { value: "+69%", label: "Revenue trend" },
            { value: "7", label: "active tariffs" },
            { value: "4", label: "BikeBoxen" },
          ],
          quote:
            "\u201CWe rent e-MTBs across several bike boxes. Tariffs, bookings and billing run automatically — we take care of the bikes, not the software.\u201D",
          authorRole: "Thorsten Schmitt",
          authorOrg: "Owner, Allgäutissimo Bikeshop",
          authorInitials: "AB",
        },
      ],
      moreKicker: "More operators",
      moreTitle: "In good company.",
      more: [
        {
          name: "Stadt Korneuburg",
          type: "Municipality",
          description:
            "Digital mobility infrastructure for citizens — bike parking and more in one app.",
        },
        {
          name: "BikeBox",
          type: "Hardware partner",
          description:
            "Secure, retrofittable bike boxes, bookable and controllable through JUHUU.",
        },
        {
          name: "NÖ.Regional",
          type: "Regional development",
          description:
            "Regional sharing projects bundled into one platform instead of many silos.",
        },
      ],
      finalCtaTitle: "Become the next operator.",
      finalCtaDescription:
        "In a free demo we'll show you how your business runs with JUHUU.",
      finalCtaButton: "Book a demo",
    },
    industries: {
      kicker: "Industries",
      heroTitleLine1: "Your offer.",
      heroTitleLine2: "Our platform.",
      heroDescription:
        "Whether secure bike parking or a full rental operation — JUHUU brings your offer into the app and runs it from one dashboard. And if your case is special, we build it together.",
      chipParking: "Bike parking",
      chipRental: "Bike Rental",
      chipCustom: "Other use cases",
      parkingTitle: "Bike parking",
      parkingDescription:
        "Bike boxes and secure rooms at the station, in the city center or at work. Rented automatically, opened by QR scan. With no on-site staff.",
      parkingBullets: [
        "Daily, monthly or long-term rental",
        "Retrofittable locking systems",
        "Live occupancy in the dashboard",
      ],
      parkingStatValue: "50+",
      parkingStatLabel: "secure spots (ÖBB Bike:Parking)",
      parkingStatNote:
        "Weather-protected, monitored, theft-proof opened at the push of a button via the app.",
      rentalTitle: "Bike Rental",
      rentalDescription:
        "From a single bike shop to a fleet: rent bikes and e-bikes flexibly. 24/7 self-service, with precise billing.",
      rentalBullets: [
        "Pay-as-you-go, daily & multi-day tariffs",
        "Hand-over by QR scan or bike box",
        "Your branding in the app",
      ],
      rentalStatValue: "+69%",
      rentalStatLabel: "revenue trend (Allgäutissimo Bikeshop)",
      rentalStatNote:
        "E-MTBs rented across several bike boxes — tariffs, bookings and billing run automatically.",
      customBadge: "Your own use case",
      customTitleLine1: "Your offer doesn't",
      customTitleLine2: "fit a box?",
      customDescription:
        "JUHUU is modular. Almost any smart device or service can be connected — from charging infrastructure to lockers to equipment rental. We build your use case together with you, instead of forcing you into a standard product.",
      customCta: "Discuss your use case",
      customTiles: ["Charging", "Lockers", "Equipment rental", "Your use case"],
      finalCtaTitle: "Let's talk about your case.",
      finalCtaDescription:
        "In a free demo we'll show you how your offer goes live with JUHUU.",
      finalCtaButton: "Book a demo",
    },
    lockingSystems: {
      heroBadge: "Locking systems · Hardware",
      heroTitleLine1: "Infrastructure,",
      heroTitleHighlight: "controlled by the app",
      heroTitleSuffix: ".",
      heroDescription:
        "The locking system is the bridge between JUHUU and the real world: from the standalone lock through the connected control kit and the JUHUU cloud all the way to the app users unlock with.",
      ctaAdvice: "Request advice",
      ctaSeeKits: "See the kits",
      diagramAlt:
        "JUHUU locking system — from the locking unit through the control kit and the JUHUU cloud to the app",
      sysNodes: [
        {
          title: "Locking unit",
          description:
            "The lock on site, standalone like Tapkey or driven via the relay kit.",
        },
        {
          title: "Control Kit",
          description:
            "Power, internet connection & control unit — the connected core on site.",
        },
        {
          title: "JUHUU cloud",
          description:
            "Secure real-time control — every action is logged and visible in the dashboard.",
        },
        {
          title: "App & web app",
          description: "Users unlock via app, web app, card or terminal.",
        },
      ],
      explainerKicker: "What locking systems are for",
      explainerTitleLine1: "Where software meets",
      explainerTitleLine2: "physical hardware.",
      explainerLead:
        "Through the locking system JUHUU connects the app to real devices on site. You set tariffs and access in the dashboard — unlocking and control happen in real time.",
      explainerCards: [
        {
          title: "Standard solutions",
          description:
            "For common locking mechanisms like sliding doors, boxes, cabinets we have ready-made kits that work out of the box.",
        },
        {
          title: "Relay kit for special cases",
          description:
            "Anything that needs special control is additionally driven via the relay kit on the control kit, almost any device.",
        },
        {
          title: "App & web app control",
          description:
            "End users unlock via app or web app — you keep every action in view, live in the JUHUU dashboard.",
        },
      ],
      kitsKicker: "Our kits",
      kitsTitle: "A kit for every use case.",
      labelUseCase: "UseCase",
      labelPlugAndPlay: "Plug & Play",
      plugAndPlayText:
        "In the standard version ready to go out of the box — plug in and start.",
      kits: [
        {
          badge: "Standalone",
          index: "01 — Tapkey",
          title: "Tapkey lock",
          description:
            "The electronic locking cylinder that runs entirely on its own — battery-powered, no wiring. Just swap the existing cylinder and unlock via app, NFC card or tag.",
          useCase:
            "Doors to bike rooms, collective garages, storage and utility rooms or existing bike boxes. Wherever no wiring is possible and the lock must run on its own.",
          specs: [
            { k: "Type", v: "Electronic locking cylinder (Euro profile)" },
            { k: "Operation", v: "Standalone, battery-powered" },
            { k: "Power", v: "2× lithium CR2, 3.0 V" },
            { k: "Battery life", v: "up to 25,000 cycles / 3 years" },
            { k: "Lengths", v: "40–100 mm (to 180 mm on request)" },
            { k: "Durability", v: "≥ 100,000 cycles (EN 15684 cl. 6)" },
            { k: "Protection", v: "IP66 outer knob · IP65 cylinder" },
            { k: "Temperature", v: "−25 °C … +65 °C" },
            { k: "Access", v: "NFC 13.56 MHz · MIFARE DESFire EV2 (AES-128)" },
            { k: "Operation", v: "Android (BLE & NFC) · iPhone (BLE)" },
            { k: "Certification", v: "EN 15684 · fire resistance T90" },
          ],
        },
        {
          badge: "Control unit",
          index: "02 — Control Kit",
          title: "Control Kit",
          description:
            "The connected core: it establishes the internet connection, runs on mains power and carries the control unit. From here JUHUU controls everything in real time from the cloud — and the relay kits attach to the control kit.",
          useCase:
            "The backbone of every connected site — wherever a location is controlled online. It powers the relay kits and reports every action to the JUHUU cloud.",
          specs: [
            { k: "Type", v: "Control unit / gateway" },
            { k: "Function", v: "Internet link & control" },
            { k: "Connectivity", v: "Mobilfunk (LTE/NB-IoT) · WLAN · Bluetooth" },
            { k: "Power", v: "12–24 V DC (mains)" },
            { k: "Control", v: "JUHUU cloud & dashboard, real time" },
            { k: "Expansion", v: "Relay kits attachable" },
            { k: "Deployment", v: "One unit per site" },
          ],
        },
        {
          badge: "Relay",
          index: "03 — Relay Kit",
          title: "Relay Kit",
          description:
            "The switching kit on the control kit: it drives locks — or anything that can be controlled via a relay. 8 power relays per kit, easily cascaded for larger sites.",
          useCase:
            "Drives locks, barriers, motors, lights and custom hardware — anything that can be switched via a relay.",
          specs: [
            { k: "Type", v: "Relay switching kit" },
            { k: "Power", v: "12–24 V DC (red +, black −)" },
            { k: "Relay channels", v: "8 × with optocouplers & protection" },
            { k: "Relay modules", v: "8 × power relays" },
            { k: "Connections", v: "Screw terminals · NO/COM/NC · data/GND" },
            { k: "Linking", v: "to control kit, cascadable" },
            { k: "Enclosure", v: "Sealed plastic, IP65" },
            { k: "Mounting", v: "Mounting plate, strain relief, cable set" },
          ],
        },
        {
          badge: "Standalone · electronic",
          index: "04 — EMZ",
          title: "EMZ electronics kit",
          description:
            "A fully standalone kit with its own electronic locks — motor-driven, so no manual turning like a cylinder. The standalone alternative to Tapkey when you need electric locks: waterproof, weatherproof and up to 2 years of battery life.",
          useCase:
            "Bike boxes and facilities that need standalone, electric locking — without wiring and without manual closing.",
          specs: [
            { k: "Type", v: "Standalone kit with electronic locks" },
            { k: "Lock", v: "Motor-driven (no manual turning)" },
            { k: "Operation", v: "Standalone, battery-powered" },
            { k: "Battery life", v: "up to 2 years" },
            { k: "Protection", v: "Waterproof & weatherproof" },
            { k: "Access", v: "App · NFC" },
            { k: "Control", v: "Integrated into JUHUU" },
          ],
        },
      ],
      accessoriesKicker: "Add-ons & extensions",
      accessoriesTitle: "More than the lock.",
      accessoriesLead:
        "Around the kits we offer the hardware a professional site needs — from the on-site terminal to occupancy sensors.",
      accessories: [
        {
          title: "On-site terminal",
          description:
            "A weatherproof self-service kiosk for anyone who wants to book without a smartphone: pick a spot or box on the touchscreen, pay contactless (card, Apple Pay, Google Pay) and unlock right away. Connected to the same JUHUU dashboard as the app and web app.",
        },
        {
          title: "Power & solar supply",
          description:
            "Power supplies, battery buffers and solar options for sites without a fixed power connection — so the control and relay kit run reliably off-grid too.",
        },
        {
          title: "RFID & card reader",
          description:
            "Access by NFC/RFID card — for transit cards, employee badges or issued tags. A great alternative to the smartphone at high-traffic sites.",
        },
        {
          title: "Occupancy sensors",
          description:
            "Sensors detect whether a box or spot is free or occupied — availability shows live in the app, web app and dashboard.",
        },
      ],
      dailyKicker: "Built for daily use",
      dailyTitleLine1: "Rugged, retrofittable,",
      dailyTitleLine2: "low-maintenance.",
      dailyDescription:
        "Our locking systems are built for continuous use and retrofit to existing hardware — without a costly replacement.",
      dailyBullets: [
        "App, web app & card access",
        "Retrofits doors, boxes & sliding doors",
        "Weatherproof components for outdoor use",
        "Centrally controlled & logged in the JUHUU dashboard",
      ],
      dailyPhotoPlaceholder: "Drop a photo of on-site use here",
      dailyPhotoAlt: "JUHUU locking systems in everyday use on site",
      finalCtaTitle: "Does it fit your hardware?",
      finalCtaDescription:
        "Send us your use case — we'll check retrofit options and recommend the right kit.",
      finalCtaButton: "Request advice",
    },
    home: {
      page: {
        heroBadge: "All-in-one sharing platform",
        heroTitle: "The platform for modern",
        heroHighlight: "mobility & infrastructure",
        heroDescription:
          "We digitalize your infrastructure. Connect your smart device, offer it in the JUHUU app and control everything from our dashboard.",
        heroCtaDemo: "Book a demo",
        heroCtaProducts: "See products",
        heroChecks: [
          "Plug & play hardware",
          "Interface & tariffs set up by us",
          "Ready to go out of the box",
        ],
        trustedBy: "Trusted by",
        offerKicker: "What we offer",
        offerTitleLine1: "One toolkit.",
        offerTitleLine2: "For cities and manufacturers.",
        offerDescription:
          "App, web app, booking platform, device control and the matching electronics — from a single source. The complete toolkit to make your infrastructure digital.",
        citiesKicker: "Cities · Municipalities · Public sector",
        citiesTitle: "Digital services for your citizens.",
        citiesDescription:
          "We deliver app, web app and booking platform — including device control and matching electronics. Launch bike parking, rental and shared mobility, but also any other process where your citizens find, book and control something.",
        citiesBullets: [
          "App, web app & booking platform from one source",
          "Device control & matching electronics included",
          "Bike parking, rental, shared mobility & more",
          "Also for bike & scooter sharing with no physical stations at all",
        ],
        manufacturersKicker: "Hardware manufacturers",
        manufacturersTitle: "Your hardware — with JUHUU software.",
        manufacturersDescription:
          "Two ways to make your devices bookable and controllable:",
        manufacturersOptionATitle: "Straight into the JUHUU app",
        manufacturersOptionADescription:
          "Bring your devices into the JUHUU app and sell the software alongside — instantly visible to all existing users.",
        manufacturersOptionBTitle: "Complete whitelabel solution",
        manufacturersOptionBDescription:
          "Get a ready-made whitelabel solution with your hardware connected — JUHUU runs in the background. Sell it under your own brand, ideal for tenders.",
        productsKicker: "Three building blocks for first-class infrastructure",
        productsTitle: "App - Dashboard - Locking systems",
        productsLead:
          "From the physical device to the booking in the app. JUHUU covers the entire process.",
        appDeployCells: [
          {
            tag: "No install",
            title: "WebApp",
            description:
              "The same functionality straight in the browser — ideal for an on-site QR entry, with no store download.",
          },
          {
            tag: "In the JUHUU app",
            title: "JUHUU app",
            description:
              "Your section appears inside the JUHUU app — with your colours, wording, logo & map marker. Instantly visible to existing users.",
          },
          {
            tag: "Own app",
            title: "Whitelabel app",
            description:
              "A fully standalone app in your look — app icon, loading screen, map, buttons and the entire appearance under your brand.",
          },
        ],
        payStripKicker: "All common payment methods · via Stripe",
        includedKicker: "Included in every variant",
        includedItems: [
          "Guest mode — discover the app without an account",
          "Optional identity verification (ID / licence)",
          "14+ languages, one secure checkout",
        ],
        dashboardTitleAlt1: "Your business.",
        dashboardTitleAlt2: "In one cockpit.",
        dashboardLead:
          "Manage devices, set tariffs, automate billing and see in real time what's happening. Predictive maintenance and analytics included — without your own tech team.",
        dashboardBullets: [
          "Real-time monitoring of every device & site",
          "Freely configurable tariffs, per-second billing",
          "Automated invoices straight into your accounting",
          "Your branding: logo & colours in the app",
        ],
        dashboardChips: [
          "Bookings",
          "Devices",
          "Tariffs",
          "Billing",
          "Customers",
          "Benefit cards",
          "Incidents",
        ],
        dashboardCta: "See the dashboard",
        hardwareSpecs: [
          ["IP66", "3 yr battery", "NFC · BLE", "EN 15684"],
          ["LTE · WLAN · BLE", "12–24 V DC", "Real time"],
          ["8 relays", "IP65", "NO/COM/NC"],
          ["Motor-driven", "to 2 yr battery", "Waterproof"],
        ],
        appKicker: "JUHUU App",
        appTitleLine1: "One app.",
        appTitleLine2: "Everything the city shares.",
        appDescription:
          "End users discover, book and unlock devices from many providers, in one intuitive app. Without many hurdles, with transparent pricing and secure payment.",
        appBullets: [
          "Guest mode: find every offer without an account",
          "Scan a QR code to use a service in seconds",
          "Shared mobility, infrastructure & tourism in one platform",
          "14+ languages, one secure checkout",
        ],
        appCta: "Explore the app",
        dashboardKicker: "JUHUU Dashboard",
        dashboardTitleLine1: "Dashboard.",
        dashboardTitleLine2: "Here's what's happening right now.",
        dashboardNavHome: "Home",
        dashboardNavOperators: "Operators",
        dashboardNavBooking: "Booking",
        dashboardNavDevices: "Devices",
        dashboardNavBilling: "Billing",
        dashboardNavSupport: "Support",
        dashboardKpis: [
          { label: "Revenue (7 d)", value: "3.000 €", delta: "▲ 69,4 %" },
          { label: "Rentals", value: "12", delta: "▲ 33,3 %" },
          { label: "Active devices", value: "318", delta: "6 offline" },
          { label: "Utilization", value: "74 %", delta: "avg 7 d" },
        ],
        dashboardIncidents: "Open incidents",
        dashboardIncidentAwaiting: "Awaiting",
        lockingKicker: "Locking systems",
        lockingTagPill: "Hardware",
        lockingTitleLine1: "Smart locks",
        lockingTitleLine2: "that just work.",
        lockingDescription:
          "From bike rooms to lockers: JUHUU locking systems connect the physical world to the app. Scan a QR, unlock, go — wireless, weatherproof and retrofittable to existing hardware.",
        lockingBullets: [
          "QR & Bluetooth access, even offline",
          "Retrofittable, no new infrastructure needed",
          "Weatherproof, monitored, theft-proof",
          "Centrally controlled from the dashboard",
        ],
        lockingCta: "Explore locking systems",
        hardwareItems: [
          {
            name: "Tapkey",
            tag: "Standalone",
            title: "Electronic locking cylinder",
            description:
              "Runs entirely on its own — battery-powered, no wiring. Swap the existing cylinder and unlock via app, NFC card or tag.",
          },
          {
            name: "Control Kit",
            tag: "Control unit",
            title: "Power, internet & control",
            description:
              "The connected core: establishes the internet connection, runs on mains power and controls in real time from the cloud. The relay kits attach here.",
          },
          {
            name: "Relay Kit",
            tag: "Relay",
            title: "Switches (almost) anything",
            description:
              "The switching kit on the control kit: drives locks, barriers, motors, lights and custom hardware — anything switchable via a relay. Cascadable for large sites.",
          },
          {
            name: "EMZ",
            tag: "Standalone · electronic",
            title: "Own electronic locks",
            description:
              "A standalone kit with motor-driven locks — no manual turning. The standalone alternative to Tapkey for electric locks: waterproof with up to 2 years of battery.",
          },
        ],
        stepsKicker: "How simple it is",
        stepsTitleLine1: "Plug & play",
        stepsTitleLine2: "ready in 4 steps.",
        stepsDescription:
          "You buy the locking system from us — we take care of the rest. Consulting, setup and tariffs included, so you launch without your own tech team.",
        steps: [
          {
            title: "Consultation",
            description:
              "We analyse your use case and recommend the right locking system for your site.",
          },
          {
            title: "Plug & play hardware",
            description:
              "You receive the locking system and hardware preconfigured — plug in and done, with zero development.",
          },
          {
            title: "We set it all up",
            description:
              "We configure interface, branding and tariffs for you in the dashboard — tailored to your offer.",
          },
          {
            title: "Ready to go",
            description:
              "Your offer is live in app, web app & dashboard — your customers book and use it easily.",
          },
        ],
        industriesKicker: "Who we build for",
        industriesTitle: "Your industry. Our platform.",
        industries: [
          {
            title: "Bike parking",
            description: "Bike boxes & secure rooms at the station.",
          },
          {
            title: "Bike Rental",
            description: "Rent bikes & e-bikes flexibly, around the clock.",
          },
          {
            title: "Other use cases",
            description: "Your case is special? We'll build it.",
          },
        ],
        pricingKicker: "Pricing",
        pricingTitleLine1: "Enterprise quality.",
        pricingTitleLine2: "For every size.",
        pricingDescription:
          "A platform that grows with you — from a single site to a nationwide network. You only pay for what you use.",
        pricingPoints: [
          "Cancel monthly",
          "No setup fee for standard integrations",
          "Scaling commission instead of fixed-cost traps",
        ],
        pricingCardLabel: "App + Dashboard + locking",
        pricingFrom: "from €149",
        pricingPeriod: "/month for 50 spaces",
        pricingNote:
          "+ a fair commission per booking. Scales with your business.",
        pricingIncludes: [
          "Visibility in the JUHUU app",
          "Full dashboard with analytics",
          "Automated billing & invoicing",
          "Custom branding & interface",
        ],
        pricingCta: "Request a quote",
        pricingBadges: ["Cancel monthly", "GDPR-compliant", "EU servers"],
        faqKicker: "FAQ",
        faqTitle: "Frequently asked.",
        faqs: [
          {
            q: "What exactly is JUHUU?",
            a: "JUHUU is a modular all-in-one platform for the sharing economy. We deliver the full software stack — app, dashboard, billing, support — plus smart locking systems so you can offer any device or service in days instead of months.",
          },
          {
            q: "Do I need to build my own app?",
            a: "No. Your offer appears directly in the JUHUU app and is instantly visible to all existing users. Optionally with your own branding and a custom interface.",
          },
          {
            q: "How long does integration take?",
            a: "Typically under two months. Via no-code templates, low-code configurators or our API we connect devices from many manufacturers — without you needing your own development.",
          },
          {
            q: "Do your locking systems work with my hardware?",
            a: "In most cases, yes. JUHUU locking systems are retrofittable and designed for common doors, boxes and cabinets. We'll check your specific case in the demo call.",
          },
          {
            q: "How does billing work?",
            a: "Fully automated. You set tariffs in the dashboard, JUHUU handles payment and invoicing and can feed it straight into your accounting system.",
          },
          {
            q: "Is JUHUU GDPR-compliant?",
            a: "Yes. All data is processed on EU servers and we comply with GDPR. We're happy to go through the details with you.",
          },
        ],
        finalCtaTitle: "Ready to bring your device online?",
        finalCtaDescription:
          "In a free demo we'll show you how fast your offer goes live in the JUHUU app.",
        finalCtaButton: "Book a free demo",
        finalStats: [
          { value: "< 2", label: "months", sub: "to live" },
          { value: "GDPR", label: "EU servers", sub: "" },
        ],
      },
      heroTitle: "The Platform for Modern",
      heroHighlight: "Mobility & Infrastructure",
      heroDescription:
        "JUHUU is a modular platform that helps businesses build, launch, and scale digital solutions for shared mobility, smart infrastructure, and utilities—all in one place.",
      getStarted: "Get started",
      fastDeployment: "Fast deployment",
      modularDesign: "Modular design",
      aiEnhanced: "AI-enhanced",
      productCategoriesTitle: "Our Product Categories",
      productCategoriesDescription:
        "Discover our comprehensive suite of solutions designed to meet your business needs",
      emailPlaceholder: "Email address",
      dashboardAlt: "JUHUU Dashboard",
      components: {
        useCases: {
          title: "Solutions for Every Need",
          description:
            "Discover how JUHUU adapts to different industries and use cases",
          designModel: {
            title: "Design Your Business Model",
            description:
              "JUHUU provides a comprehensive toolkit for OEMs and operators to create their own unique business models...",
            tags: [
              "Modular Components",
              "Flexible Integration",
              "Custom Revenue Models",
              "Rapid Deployment",
            ],
            cta: "Explore Possibilities",
            imgAlt: "Custom Business Model",
          },
          bikeParking: {
            title: "Bike Parking",
            description:
              "Our comprehensive bike parking solution serves both OEMs and operators...",
            tags: [
              "Hardware Integration",
              "Access Management",
              "Utilization Analytics",
              "Subscription Models",
            ],
            cta: "Discover Bike Parking",
            imgAlt: "Bike Parking Solution",
          },
          smartCity: {
            title: "Smart City",
            description:
              "JUHUU equips cities with a comprehensive toolkit to become smart cities, integrating all their services into a central platform",
            tags: ["Fleet Management", "User Apps", "Maintenance", "Analytics"],
            cta: "Discover Smart City",
            imgAlt: "Bike Sharing",
          },
        },
        targetGroups: {
          title: "Empowering Your Digital Ecosystem",
          description:
            "Our modular SaaS platform provides tailored solutions for different stakeholders in the mobility and infrastructure space",
          oems: {
            title: "For OEMs",
            description:
              "Integrate your hardware into our ecosystem and expand your market reach. Our platform provides tools to digitize your products, monitor performance, and create new revenue streams.",
            features: {
              digitalTwins: {
                title: "Digital Twins",
                desc: "Create virtual replicas of your hardware",
              },
              integrationOptions: {
                title: "Integration Options",
                desc: "No-code, Low-code & Expert solutions",
              },
              marketplace: {
                title: "Marketplace",
                desc: "Showcase your products to operators",
              },
              analytics: {
                title: "Analytics",
                desc: "Gain insights from usage data",
              },
            },
            imageAlt: "OEM devices",
          },
          operators: {
            title: "For Operators",
            description:
              "Build, launch, and scale your digital operations rapidly with our modular building blocks. Create tailored solutions for shared mobility, smart infrastructure, and utilities without starting from scratch.",
            features: {
              modularPlatform: {
                title: "Modular Platform",
                desc: "Mix and match components as needed",
              },
              dashboard: {
                title: "Dashboard",
                desc: "Monitor and manage your operations",
              },
              billing: {
                title: "Billing",
                desc: "Handle payments and subscriptions",
              },
              userApps: {
                title: "User Apps",
                desc: "Provide seamless customer experiences",
              },
            },
            imageAlt: "Operator dashboard",
          },
        },
        productCategories: {
          coreInfrastructure: {
            tag: "Core Infrastructure & Developer Tools",
            title: "Build the foundation for innovation",
            description:
              "This category forms the backbone of the JUHUU ecosystem, providing essential infrastructure and developer tools to enable rapid development, secure integration, and scalable operations for shared mobility and smart infrastructure businesses. It empowers operators and OEMs with a solid foundation to innovate and deploy solutions efficiently.",
            identity: {
              title: "JUHUU Identity",
              description:
                "Central authentication service offering email/password, Google/Apple Sign-In, and optional ID-document verification.",
              cta: "Identity Solutions",
              alt: "JUHUU Identity",
            },
            docs: {
              title: "JUHUU Docs",
              description:
                "A comprehensive knowledge base where OEMs and operators upload manuals, technical specs, and policies.",
              cta: "Documentation Hub",
              alt: "JUHUU Docs",
            },
            specialDevelopment: {
              title: "JUHUU Special Development",
              description:
                "A bespoke integration service for complex use cases, with custom systems embedded into the platform.",
              cta: "Custom Solutions",
              alt: "JUHUU Special Development",
            },
            digitalTwins: {
              title: "JUHUU Digital Twins",
              description:
                "A unified hub creating virtual replicas of integrated devices, allowing operators to monitor health and status.",
              cta: "Digital Twin Platform",
              alt: "JUHUU Digital Twins",
            },
            developerToolsLabel: "Developer Tools",
            bigImageAlt: "Core Infrastructure & Developer Tools",
          },
          userAccess: {
            tag: "User Access & Booking Interfaces",
            title: "Seamless access, anytime, anywhere",
            description:
              "This category focuses on delivering user-friendly interfaces that simplify booking and accessing mobility and infrastructure services across multiple platforms.",
            app: {
              title: "JUHUU App",
              description:
                "A native iOS/Android application enabling users to find, book, and access services like bikes, scooters, and lockers through a single interface, with white-labeling for operators.",
              cta: "App Features",
              label: "Mobile App",
              altIcon: "JUHUU App",
              altImage: "JUHUU Mobile App",
              appStoreAlt: "Download on the App Store",
              googlePlayAlt: "Get it on Google Play",
            },
            webApp: {
              title: "JUHUU Web App",
              description:
                "A browser-based alternative to the JUHUU App, providing full booking and unlocking functionality for occasional users, with a streamlined feature set to encourage native app adoption.",
              cta: "Web App Platform",
              altIcon: "JUHUU Web App",
            },
            map: {
              title: "JUHUU Map",
              description:
                "An interactive map aggregating all service locations, offering customizable filters and operator branding, with iframe embedding options for external platforms at an additional cost.",
              cta: "Interactive Map",
              altIcon: "JUHUU Map",
            },
          },
          commerce: {
            tag: "Commerce & Monetization",
            title: "Transform transactions into growth",
            description:
              "Powerful tools to manage payments, create new revenue streams, and build sustainable business models. Our commerce solutions help you monetize services and maximize profitability.",
            billing: {
              title: "JUHUU Billing",
              tagline: "Complete payment infrastructure",
              description:
                "A comprehensive financial hub managing payments, automated invoice generation, operator payouts, and tariff setup. Built on Stripe's infrastructure with real-time Dashboard integration.",
              stats: {
                success: "Payment success rate",
                speed: "Faster processing",
                revenue: "Revenue increase",
              },
              cta: "Learn more about Billing",
              altIcon: "JUHUU Billing",
              altImage: "JUHUU Billing Dashboard",
            },
            marketplace: {
              title: "JUHUU Marketplace",
              description:
                "A sales channel where OEMs list certified, platform-integrated devices and services, allowing operators to browse and adopt solutions with ease.",
              cta: "Explore Marketplace",
              altIcon: "JUHUU Marketplace",
            },
            revenueShare: {
              title: "JUHUU Revenue Share",
              comingSoon: "Coming Soon",
              description:
                "An upcoming affiliate-style program rewarding operators with 15% of platform fees for user referrals, designed to lower acquisition costs and boost growth.",
              altIcon: "JUHUU Revenue Share",
            },
            plusSubscriptions: {
              title: "Plus Subscriptions",
              comingSoon: "Coming Soon",
              description:
                "Create premium subscription tiers for your services with flexible billing cycles, trial periods, and special perks for subscribers.",
              altIcon: "Plus Subscription",
            },
            testimonial: {
              quote:
                "\"JUHUU's billing system has transformed our financial operations. We've reduced payment processing time by 60% and increased revenue collection efficiency by 35% since implementation.\"",
              name: "Thomas Weber",
              position: "CFO at Urban Mobility Solutions",
              altPortrait: "Customer Portrait",
            },
            ctaTitle: "Ready to transform your revenue model?",
            ctaButton: "Contact Sales",
          },
          operations: {
            tag: "Operations and Monitoring",
            title: "Control and optimize your operations",
            description:
              "This category provides advanced tools for monitoring, managing, and optimizing operations in real-time. Aimed at operators and OEMs, it offers comprehensive oversight of assets, predictive maintenance, and performance analytics, helping to minimize downtime and enhance efficiency.",
            dashboard: {
              title: "JUHUU Dashboard",
              description:
                "The central command center for operators and OEMs, offering real-time monitoring of revenues, device status, utilization, and support interventions. Get comprehensive insights into your business performance with customizable views and reports.",
              cta: "Dashboard Features",
              altIcon: "Dashboard",
            },
            anomalyDetection: {
              title: "JUHUU AI Anomaly Detection",
              comingSoon: "Coming Soon",
              description:
                "A next-generation maintenance module using ML and LLM to predict failures, reducing unplanned downtime by up to 30% and maintenance costs by 25%, with alerts in the Dashboard. Stay ahead of issues before they impact your business.",
              altIcon: "JUHUU AI Anomaly Detection",
            },
            demoTitle: "Dashboard Demo",
          },
          customerSupport: {
            tag: "Customer Support",
            title: "Support that never sleeps",
            description:
              "This category delivers continuous, intelligent support solutions to enhance user and operator experiences. Leveraging AI and human expertise, it provides immediate assistance and escalates complex issues, ensuring high satisfaction and reducing support costs.",
            buddy: {
              title: "JUHUU Buddy",
              description:
                "An AI-powered chatbot trained on JUHUU Docs, embedded across platforms to answer questions and execute tasks like creating locations or updating tariffs. Available 24/7, it provides instant support for common queries and operational tasks.",
              bullets: [
                "Trained on your documentation",
                "Performs operational tasks automatically",
                "Available across all platforms",
              ],
              cta: "Meet JUHUU Buddy",
              overlay: "AI-Powered",
              altIcon: "JUHUU Buddy",
              altImage: "JUHUU Buddy",
            },
            customerSupport: {
              title: "JUHUU Customer Support",
              description:
                "An upcoming multimodal support suite with custom phone numbers, UI-aware context, automated escalations, and operator-specific answers from Docs. This comprehensive solution will provide seamless support experiences across all channels.",
              bullets: [
                "Multichannel support (chat, phone, email)",
                "Context-aware assistance",
                "Intelligent escalation to human agents",
              ],
              cta: "Support Solutions",
              overlay: "Coming Soon",
              altIcon: "JUHUU Customer Support",
              altImage: "JUHUU Customer Support",
            },
          },
        },
      },
    },
    app: {
      heroBadge: "The JUHUU app · For operators",
      heroTitle: "One app for your customers.",
      heroHighlight: "Under your brand.",
      heroDescription:
        "Shared mobility, bike parking and bike rental in one app — native for iOS & Android, as a web app and as a full whitelabel. We integrate almost any bookable offer and ship the matching hardware too.",
      ctaBookDemo: "Book a demo",
      ctaSeeOptions: "See the options",
      platformIosAndroid: "iOS & Android",
      platformWebApp: "WebApp",
      platformWhitelabel: "Whitelabel",
      platform14Languages: "14+ languages",
      heroPhoneAlt: "JUHUU app — map with offers near you",
      page: {
        deployKicker: "Three ways into the app",
        deployTitle: "How your offer reaches your customers.",
        deployCells: [
          {
            tag: "In the JUHUU app",
            title: "JUHUU app",
            description:
              "Your section appears inside the JUHUU app — with your colours, wording, logo and map marker. Instantly visible to existing users.",
          },
          {
            tag: "Own app",
            title: "Whitelabel app",
            description:
              "A fully standalone app in your look — app icon, loading screen, map, buttons and the entire appearance under your brand.",
          },
          {
            tag: "No install",
            title: "WebApp",
            description:
              "The same functionality straight in the browser — ideal for an on-site QR entry, with no store download.",
          },
        ],
        partialKicker: "Partial whitelabel · in the JUHUU app",
        partialTitleLine1: "Your brand,",
        partialTitleLine2: "inside our app.",
        partialDescription:
          "Your offer lives inside the JUHUU app and is styled to your brand. Users who already have JUHUU find you instantly — with no new app to install.",
        partialBullets: [
          "Interface & button colours",
          "Your own wording & logo",
          "Custom map markers",
          "Custom functions in the background",
        ],
        standaloneKicker: "Whitelabel app · fully standalone",
        standaloneTitleLine1: "Your own app,",
        standaloneTitleLine2: "end to end.",
        standaloneDescription:
          "A fully standalone app in the store — under your name, in your design. We adapt the entire appearance to your brand.",
        standaloneBullets: [
          "Your own app icon",
          "Loading screen on app launch",
          "Map, buttons & overall look",
          "Your own presence on App Store & Google Play",
        ],
        standaloneCtaReferences: "See references",
        featuresKicker: "Included in every variant",
        featuresTitle: "Everything a booking needs.",
        featuresList: [
          {
            title: "All kinds of tariffs",
            description:
              "Pay-as-you-go, daily, monthly and annual tariffs, discounts and gift cards freely combinable.",
          },
          {
            title: "All common payment methods",
            description:
              "From debit and credit card to Apple Pay & Google Pay to PayPal.",
          },
          {
            title: "Identity verification",
            description:
              "Optionally we ask users to confirm their identity via passport, driver's licence or ID card.",
          },
        ],
        paymentsKicker: "Payment methods via Stripe",
        hardwareKicker: "Hardware",
        hardwareTitleLine1: "We offer everything —",
        hardwareTitleLine2: "from autonomous locks to relays.",
        hardwareLead:
          "We offer everything to cover your use case: from ready-made locking systems that run fully autonomously, to setups we drive through our relay boards. And we ship the hardware with it — from the locks to the IoT connectivity.",
        hardwareCards: [
          {
            title: "Ready-made locking systems",
            description:
              "Complete locking systems that run autonomously — doors, boxes, cabinets and sliding doors, retrofit included. We supply the locks and the IoT connectivity too.",
          },
          {
            title: "Integrate new hardware",
            description:
              "Got special devices? We connect new hardware to the platform — via no-code templates, configurators or our API.",
          },
          {
            title: "JUHUU relay board",
            description:
              "Full freedom: with our relay board you decide what gets triggered — barriers, locks, lights, motors or whatever your use case needs.",
          },
        ],
        useCasesKicker: "Use cases",
        useCasesTitle: "Standard use cases — or your own.",
        useCasesDescription:
          "Bike parking, bike rental and shared mobility come as standard. Anything beyond that you build with our standard software & electronics — or fully freely via the relay board.",
        useCaseBikeParking: "Bike parking",
        useCaseBikeRental: "Bike Rental",
        useCaseSharedMobility: "Shared mobility",
        useCaseStandard: "Standard",
        useCaseOwnTitle: "Your own use case",
        useCaseOwnSubtitle: "Software / electronics / relay",
        logosTitle: "Trusted by",
        finalCtaTitle: "Which variant fits you?",
        finalCtaDescription:
          "In a free demo, find out which JUHUU app solution fits your offer best.",
      },
      emailPlaceholder: "Email address",
      getStarted: "Get started",
      fastDeployment: "Fast deployment",
      modularDesign: "Modular design",
      aiEnhanced: "AI-enhanced",
      bookingTitle: "Seamless Booking Experience",
      bookingDescription:
        "From discovery to usage in just a few taps - see how your customers can easily access your services",
      timeline: {
        scan: "Scan",
        select: "Select",
        pay: "Pay",
        use: "Use",
      },
      steps: [
        {
          title: "Scan QR-Code",
          desc: "Simply open the app and scan the QR code to discover available services near you.",
        },
        {
          title: "Select Tariff",
          desc: "Browse available options and choose the pricing plan that best fits your needs.",
        },
        {
          title: "Pay",
          desc: "Complete your booking with a secure payment using your preferred payment method.",
        },
        {
          title: "Use Product",
          desc: "Tap to unlock and start using the service immediately. No keys or cards needed.",
        },
      ],
      downloadText: "Download the JUHUU app and start exploring today",
      featuresTag: "Features",
      featuresTitle: "Comprehensive Platform",
      featuresDescription:
        "Our JUHUU WebApp offers a complete suite of tools for managing mobility services, infrastructure, and utilities",
      customInterfaceTag: "Customizable Interface",
      customInterfaceTitle: "Your Brand, Our Technology",
      customInterfaceDescription:
        "JUHUU enables providers to seamlessly integrate their own corporate identity into the app. Maintain your brand identity while leveraging our powerful platform.",
      becomePartner: "Become a Partner",
      customInterfaces: [
        {
          title: "Custom Branding",
          description:
            "Providers can integrate their own logo, colors, and design into the app.",
          features: [
            "Incorporate your own logo",
            "Apply your brand colors",
            "Customize product names",
            "Consistent brand experience",
          ],
        },
        {
          title: "Tailored Pricing",
          description:
            "Flexible tariff design and product categorization according to your needs.",
          features: [
            "Custom tariff structures",
            "Special discounts and offers",
            "Time-based or usage-based billing",
            "Flexible pricing options",
          ],
        },
        {
          title: "Seamless Integration",
          description:
            "Complete integration into existing brand identities like ÖBB Bike.",
          features: [
            "Consistency with other brand channels",
            "Familiar interface for customers",
            "Customizable feature labels",
            "Your own terms of use",
          ],
        },
      ],
      liveDemoTag: "Live Demo",
      liveDemoTitle: "Explore JUHUU in Action",
      liveDemoDescription:
        "Discover available locations, operators, and services in real-time. See how easy it is to find and book mobility services across our growing network.",
      mapHeading: "Interactive Map Experience",
      mapDescription:
        "Browse through our interactive map to see all available services in your area. Explore different operators, locations, and mobility options all in one place.",
      statProviders: "Service providers",
      statBookingTime: "Average booking time",
      statAvailability: "Service availability",
      partners: "Already partnering with us",
      joinOperator: "Join us as an operator",
      appStoreAlt: "Download on the App Store",
      googlePlayAlt: "Get it on Google Play",
      viewCustomInterface: "View custom interface",
      featuresList: [
        {
          title: "Mobility",
          description:
            "Comprehensive mobility solutions for urban transportation needs.",
          benefits: [
            "Seamless integration with public transport",
            "Real-time tracking and notifications",
            "Flexible payment options",
            "Multi-modal journey planning",
          ],
        },
        {
          title: "Infrastructure",
          description:
            "Smart infrastructure management for cities and businesses.",
          benefits: [
            "IoT-enabled monitoring systems",
            "Predictive maintenance",
            "Resource optimization",
            "Environmental impact tracking",
          ],
        },
        {
          title: "Utilities",
          description:
            "Efficient utility management solutions for modern needs.",
          benefits: [
            "Smart metering integration",
            "Usage analytics and insights",
            "Automated billing systems",
            "Consumption optimization",
          ],
        },
        {
          title: "Payments",
          description:
            "Secure and flexible payment processing for all services.",
          benefits: [
            "Multiple payment methods",
            "Subscription management",
            "Automated invoicing",
            "Revenue sharing capabilities",
          ],
        },
        {
          title: "Analytics",
          description: "Powerful analytics tools to drive business decisions.",
          benefits: [
            "Real-time dashboards",
            "Custom reporting",
            "Predictive analytics",
            "User behavior insights",
          ],
        },
        {
          title: "Integration",
          description:
            "Seamless integration with existing systems and third-party services.",
          benefits: [
            "Open API architecture",
            "Custom connectors",
            "Legacy system compatibility",
            "Plug-and-play modules",
          ],
        },
        {
          title: "Security",
          description: "Enterprise-grade security for all platform components.",
          benefits: [
            "End-to-end encryption",
            "Multi-factor authentication",
            "Regular security audits",
            "Compliance with global standards",
          ],
        },
      ],
    },
    map: {
      heroTitle: "Interactive Map for Mobility Services",
      heroDescription:
        "JUHUU Map provides an interactive mapping solution that shows real-time locations, availability, and routes for your mobility services. Embed it anywhere with our flexible iframe integration.",
      features: {
        realtimeTitle: "Real-time Tracking",
        realtimeDesc: "Live vehicle locations and availability status",
        integrationTitle: "Easy Integration",
        integrationDesc: "Simple iframe embedding for any website",
        globalTitle: "Global Coverage",
        globalDesc: "Works worldwide with local map data",
        mobileTitle: "Mobile Optimized",
        mobileDesc: "Responsive design for all devices",
      },
      liveDemoBadge: "Live Demo",
      liveMapTitle: "Live Interactive Map",
      liveMapDescription:
        "Experience our interactive map in action. This is the same map that powers mobility services across Europe.",
      builtForOperatorsTitle: "Built for Operators",
      builtForOperatorsDescription:
        "Companies using our JUHUU platform can seamlessly embed this interactive map into their own website. Only the locations and vehicles of the respective operator are displayed - perfect for a tailored customer experience.",
      bulletPoints: {
        locationsTitle: "Your Locations Only",
        locationsDesc:
          "The map displays exclusively your vehicles and stations",
        bookingTitle: "Direct Booking Available",
        bookingDesc:
          "Customers can book directly through the embedded map - just like through the JUHUU WebApp",
        brandingTitle: "Your Branding",
        brandingDesc: "Customizable to match your corporate design and brand",
        operatorTitle: "Operator Optimized",
        operatorDesc:
          "Specifically designed for service providers who want to present their services professionally",
      },
      integrationTitle: "Simple Integration",
      integrationDescription:
        "Copy this code and paste it into your website to render JUHUU Web App.",
      iframeTitle: "JUHUU Interactive Map",
    },
    marketplace: {
      heroTitle: "Welcome to the",
      heroHighlight: "JUHUU Marketplace",
      heroDescription:
        "Discover and integrate specialized solutions from our trusted partners to enhance your mobility platform. Our marketplace offers a wide range of products designed to work seamlessly with JUHUU.",
      browseTitle: "Browse Products",
      browseDescription:
        "Filter and search through our curated collection of mobility solutions to find the perfect fit for your needs.",
      showing: "Showing",
      of: "of",
      products: "products",
      loadMore: "Load more",
      loading: "Loading…",
      noProductsTitle: "No products found",
      noProductsDescription: "Try adjusting your search or filter criteria",
      clearFilters: "Clear all filters",
      close: "Close",
      readMore: "Read more",
      productImageAlt: "product image",
      ctaTitle: "Ready to transform your business?",
      ctaSubTitle:
        "Join hundreds of companies already using JUHUU to build, launch, and scale their mobility and infrastructure solutions.",
      ctaLaunch: "Launch your solution in weeks, not months",
      ctaScale: "Scale seamlessly as your business grows",
      ctaPay: "Pay only for what you use with our modular approach",
      ctaStart: "Get started today",
      ctaFullName: "Full Name",
      ctaWorkEmail: "Work Email",
      ctaCompany: "Company",
      ctaNamePlaceholder: "Enter your name",
      ctaEmailPlaceholder: "you@company.com",
      ctaCompanyPlaceholder: "Your company name",
      ctaSubmit: "Contact Sales",
      ctaConsent: "By submitting this form, you agree to our",
      ctaTerms: "Terms of Service",
      ctaPrivacy: "Privacy Policy",
      ctaAnd: "and",
    },
    billing: {
      heroTitle: "Unified Payments",
      heroHighlight: "& Revenue Management",
      heroDescription:
        "JUHUU Billing is the complete financial infrastructure that powers every transaction on the JUHUU platform. Built on Stripe Connect, it automates payment capture, invoicing, fee collection and monthly payouts—giving operators a single, reliable source of truth for their finances.",
      emailPlaceholder: "Email address",
      getStarted: "Get Started",
      statusLoading: "Please wait...",
      statusSent: "Sent successfully!",
      statusError: "Error, click to try again.",
      heroBulletPoints: [
        "Instant payment capture",
        "Automated invoicing",
        "Monthly payouts",
      ],
      section: {
        logoAlt: "JUHUU Billing",
        title: "Unified payments and revenue-management layer",
        slogan: "Built on Stripe Connect for seamless financial operations",
        description:
          "JUHUU Billing is the unified payments and revenue-management layer that powers every transaction on the JUHUU platform. Built on Stripe Connect, it automates payment capture, partial authorizations, invoice generation, fee collection and monthly payouts—giving operators a single, reliable source of truth for their finances.",
        primaryCta: "Get started",
        secondaryCta: "View docs",
        features: [
          {
            title: "Instant payment capture",
            description:
              "Charges customers automatically each time a booking is confirmed across all JUHUU Booking transactions.",
          },
          {
            title: "Partial authorization",
            description:
              "Places an initial card hold for pay-as-you-go rentals and settles only the final amount once usage ends.",
          },
          {
            title: "Automated invoicing",
            description:
              "Creates legally compliant invoices in the operator's name at booking completion or usage end.",
          },
          {
            title: "Fee management",
            description:
              "Deducts JUHUU transaction commissions and fixed monthly operator fees in real time with full transparency.",
          },
          {
            title: "Monthly payouts & statements",
            description:
              "Consolidates all payments, refunds and fees into a single Stripe payout with downloadable statements.",
          },
          {
            title: "Accounting Areas",
            description:
              "Groups revenue by district, city or building for granular reporting without altering invoice logic.",
          },
          {
            title: "Self-service refunds",
            description:
              "Issue full or partial refunds directly from the operator interface with automatic Stripe synchronization.",
          },
          {
            title: "Real-time transaction view",
            description:
              "See every payment, refund, fee and invoice inside JUHUU Dashboard for daily operations and support.",
          },
        ],
        stats: {
          supportedCurrencies: "Supported Currencies",
          paymentMethods: "Payment Methods",
          supportCountries: "Support Countries",
        },
      },
      featuresDetail: {
        title: "Complete Payment Infrastructure",
        description:
          "Explore the comprehensive capabilities that make JUHUU Billing the most robust payment solution for mobility operators",
        navTitle: "Key Capabilities",
        howItWorks: "How it works",
        keyBenefits: "Key Benefits",
        features: [
          {
            title: "Instant Payment Capture",
            description:
              "Charges customers automatically each time a booking is confirmed",
            details: [
              "Automatic charge processing on booking confirmation",
              "Integration with all JUHUU Booking transactions",
              "Real-time payment status updates",
              "Support for multiple payment methods",
            ],
            benefits: [
              "Reduced payment failures",
              "Immediate revenue recognition",
              "Seamless customer experience",
            ],
          },
          {
            title: "Partial Authorization",
            description:
              "Places an initial card hold for pay-as-you-go rentals and settles only the final amount",
            details: [
              "Initial card hold for estimated usage",
              "Automatic settlement of actual amount",
              "Support for hour-, minute- or second-based products",
              "Intelligent hold amount calculation",
            ],
            benefits: [
              "Reduced chargebacks",
              "Fair pricing for customers",
              "Optimized cash flow",
            ],
          },
          {
            title: "Automated Invoicing",
            description:
              "Creates legally compliant invoices in the operator's name",
            details: [
              "Legally compliant invoice generation",
              "Multi-language support",
              "Sequential numbering scheme",
              "10-year document retention",
            ],
            benefits: [
              "Tax compliance guaranteed",
              "Reduced administrative overhead",
              "Professional customer communication",
            ],
          },
          {
            title: "Monthly Payouts & Statements",
            description:
              "Consolidates all payments, refunds and fees into a single Stripe payout",
            details: [
              "Monthly automated payout processing",
              "Comprehensive CSV + PDF statements",
              "Detailed line-item breakdown",
              "Email notifications and dashboard access",
            ],
            benefits: [
              "Simplified reconciliation",
              "Complete financial transparency",
              "Streamlined accounting processes",
            ],
          },
          {
            title: "Accounting Areas",
            description:
              "Groups revenue by district, city or building for granular reporting",
            details: [
              "Virtual cost center grouping",
              "District, city, or building-level reporting",
              "No impact on invoice numbering or tax rates",
              "Read-only in invoices for compliance",
            ],
            benefits: [
              "Multi-site performance tracking",
              "Granular revenue insights",
              "Compliance-safe reporting",
            ],
          },
          {
            title: "Self-Service Refunds",
            description:
              "Issue full or partial refunds directly from the operator interface",
            details: [
              "Full and partial refund capabilities",
              "Automatic Stripe synchronization",
              "Credit note generation",
              "Audit trail maintenance",
            ],
            benefits: [
              "Faster customer service",
              "Reduced support overhead",
              "Complete refund transparency",
            ],
          },
          {
            title: "Real-Time Transaction View",
            description:
              "See every payment, refund, fee and invoice inside JUHUU Dashboard",
            details: [
              "Live transaction monitoring",
              "Complete payment history",
              "Linked invoice tracking",
              "Real-time status updates",
            ],
            benefits: [
              "Immediate issue resolution",
              "Enhanced operational visibility",
              "Proactive customer support",
            ],
          },
        ],
      },
      paymentFlow: {
        title: "Payment Flow Visualization",
        description:
          "See how JUHUU Billing processes payments from booking to dashboard visibility in real-time",
        play: "Play",
        pause: "Pause",
        reset: "Reset",
        steps: [
          {
            title: "Booking Event",
            description:
              "JUHUU Booking posts a payment intent to JUHUU Billing",
            details:
              "When a customer confirms a booking, the system automatically creates a payment intent with all necessary transaction details.",
          },
          {
            title: "Card Capture",
            description:
              "Stripe processes the charge; funds land in operator's balance",
            details:
              "Stripe securely processes the payment and transfers funds to the operator's Stripe balance, minus the JUHUU transaction fee.",
          },
          {
            title: "Partial Authorization",
            description: "For pay-as-you-go, JUHUU reserves estimated amount",
            details:
              "For flexible-duration rentals, the system places a hold for the estimated cost and converts it to the exact charge when usage ends.",
          },
          {
            title: "Invoice Generation",
            description: "PDF invoice created in customer's local language",
            details:
              "A legally compliant invoice is automatically generated in the customer's language and archived in the operator's account.",
          },
          {
            title: "Dashboard Visibility",
            description:
              "Full payment object visible in JUHUU Dashboard immediately",
            details:
              "The complete payment information, linked invoice, and any subsequent refunds are instantly visible in the dashboard.",
          },
        ],
        cards: [
          {
            title: "Security First",
            description:
              "Built on Stripe's PCI DSS Level 1 compliant infrastructure with end-to-end encryption and fraud protection.",
          },
          {
            title: "Global Scale",
            description:
              "Process payments with local payment methods and automatic currency conversion.",
          },
          {
            title: "Real-time Monitoring",
            description:
              "Track every transaction in real-time with comprehensive logging and instant notifications for any issues.",
          },
        ],
      },
      availableNow: "Available now",
    },
    docs: {
      heroTitle: "Intelligent Knowledge",
      heroHighlight: "Management",
      heroDescription:
        "JUHUU Docs transforms scattered operator knowledge into a searchable, multilingual resource. With AI-powered summaries, automatic translations, and seamless integration with JUHUU Buddy, it fuels faster support and better self-service across the entire JUHUU Platform.",
      emailPlaceholder: "Email address",
      getStarted: "Get Started",
      wait: "Please wait...",
      sent: "Sent successfully!",
      error: "Error, click to try again.",
      unlimitedArticles: "Unlimited articles",
      languages: "14 languages",
      aiSearch: "AI-powered search",
      components: {
        section1: {
          heading: "JUHUU Docs",
          description:
            "Transform scattered knowledge into a searchable, multilingual resource that powers better support and self-service across your entire platform.",
          articleManagementTitle: "Article Management",
          articleManagementSubtitle: "Unlimited content creation",
          articleManagementDescription:
            "Create and upload unlimited articles directly from JUHUU Dashboard. Keep product, pricing, and location information always up-to-date with instant publishing.",
          articleManagementItems: [
            "Unlimited articles",
            "Instant publishing",
            "Real-time updates",
          ],
          multilingualTitle: "14 Languages",
          multilingualSubtitle: "AI-powered translation",
          multilingualDescription:
            "Each article can be shown in any of 14 UI languages. Missing translations are generated on-demand by JUHUU's AI translation service the first time a user requests them.",
          multilingualLabels: {
            languages: "Languages",
            languagesDesc: "14+ supported languages",
            aiTranslation: "AI Translation",
            aiTranslationDesc: "Zero manual overhead",
          },
          aiSummariesTitle: "AI Summaries & Link Previews",
          aiSummariesDescription:
            "Every article is auto-summarized. Hovering over internal links shows the summary as a preview for faster navigation and instant context.",
          aiSummariesItems: [
            "Auto-generated",
            "Link previews",
            "Instant context",
          ],
          integratedSearchTitle: "Integrated Search",
          integratedSearchDescription:
            "Full-text and semantic search across all articles, filtered to the viewer's language. Users and support agents find the right answer in seconds.",
          integratedSearchItems: [
            "Full-text search",
            "Semantic search",
            "Language filtering",
          ],
          supportBridgeTitle: "Customer Support Bridge",
          supportBridgeDescription:
            "When JUHUU Customer Support is enabled, all articles are ingested by JUHUU Buddy so the chatbot can answer user questions with cited passages.",
          supportBridgeItems: [
            "Deflects tickets",
            "Faster resolution",
            "Consistent answers",
          ],
          cta: "Start Building Your Knowledge Base",
        },
      },
    },
    identity: {
      heroTitle: "Single Sign-On &",
      heroHighlight: "Account Management",
      heroDescription:
        "JUHUU Identity is the single sign-on and account-management backbone of the JUHUU Platform. Once a user signs in, every front-end—JUHUU App, JUHUU Web App, JUHUU Dashboard, JUHUU Docs, JUHUU Marketplace and future modules—recognises that session automatically.",
      emailPlaceholder: "Email address",
      getStarted: "Get Started",
      wait: "Please wait...",
      sent: "Sent successfully!",
      error: "Error, click to try again.",
      badges: {
        universal: "Universal SSO",
        multiTenant: "Multi-tenant identity",
        enterprise: "Enterprise security",
      },
      card: {
        title: "One Identity",
        description: "Access all JUHUU services",
        services: [
          "JUHUU App",
          "JUHUU Dashboard",
          "JUHUU Marketplace",
          "JUHUU Docs",
        ],
      },
      capabilities: {
        title: "Key Capabilities",
        description:
          "Comprehensive identity management that scales with your business needs",
        items: [
          {
            title: "Universal SSO",
            description: "One account unlocks every JUHUU product",
            benefits: "End users, operators, OEMs",
          },
          {
            title: "Multi-tenant identity",
            description:
              "Lets municipalities or enterprises connect their own OAuth/SAML providers",
            benefits: "City partners, corporate fleets",
          },
          {
            title: "Flexible authentication",
            description:
              "E-mail + password, Google Sign-In, Apple ID; magic-link flow arriving soon",
            benefits: "Low-friction onboarding",
          },
          {
            title: "Profile self-service",
            description:
              "Users update name, address or VAT ID directly in JUHUU App / Dashboard",
            benefits: "Less support workload",
          },
          {
            title: "Management Accounts",
            description:
              "Shared wallets that invite members, pay centrally and reassign bookings on the fly",
            benefits: "Companies, clubs, public bodies",
          },
        ],
      },
      authentication: {
        title: "Authentication & SSO",
        sections: [
          {
            title: "Secure JWT Authentication",
            description:
              "A secure JWT issued by JUHUU Identity accompanies every API call, so downstream services (e.g. JUHUU Booking, JUHUU Billing) trust user permissions without extra look-ups.",
          },
          {
            title: "Instantaneous Session Hand-off",
            description:
              "Sign in once on JUHUU App → open JUHUU Web App and you're already authenticated. No multiple logins required.",
          },
        ],
        flow: {
          title: "Authentication Flow",
          description: "How SSO works across JUHUU services",
          steps: [
            {
              title: "User Login",
              description: "Sign in to any JUHUU service",
            },
            {
              title: "JWT Token Issued",
              description: "Secure token for all services",
            },
            {
              title: "Access All Services",
              description: "Seamless experience everywhere",
            },
          ],
        },
      },
      accounts: {
        title: "Account Types",
        description:
          "Flexible account structures for individuals and organizations",
        highlights: "Highlights:",
        types: [
          {
            type: "Personal Account",
            holder: "Individual end users",
            highlights: [
              "Standard bookings",
              "Stored payment methods",
              "Profile edits",
            ],
          },
          {
            type: "Management Account",
            holder: "Companies, associations, municipalities",
            highlights: [
              "Add a corporate payment method",
              "Invite unlimited members",
              "Book resources once, then hand them out to members on demand",
              "Track costs centrally",
            ],
          },
        ],
        featuresTitle: "Management Account Features",
        features: [
          {
            title: "Corporate Payment",
            description: "Centralized billing and payment methods",
          },
          {
            title: "Member Management",
            description: "Invite and manage unlimited team members",
          },
          {
            title: "Usage Tracking",
            description: "Monitor costs centrally",
          },
          {
            title: "Resource Control",
            description: "Book once, distribute to members",
          },
        ],
      },
      security: {
        title: "Security Practices",
        description:
          "Enterprise-grade security built into every layer of JUHUU Identity",
        items: [
          {
            title: "Password Security",
            description:
              "Passwords are hashed with bcrypt 12 and never stored in plain text.",
          },
          {
            title: "Token Management",
            description:
              "All tokens use short-lived access (15 min) and rotating refresh tokens.",
          },
          {
            title: "Attack Prevention",
            description: "Built-in rate attack preventions.",
          },
          {
            title: "Data Residency",
            description:
              "Data residency aligns with EU GDPR and local data protection laws.",
          },
        ],
        badgeTitle: "State of the Art Security",
        uptime: "Uptime SLA",
        tokenLifetime: "Token Lifetime",
        hashRounds: "Hash Rounds",
        monitoring: "Monitoring",
      },
    },
    digitalTwins: {
      heroTitle: "Device-Management Hub of the",
      heroHighlight: "JUHUU Platform",
      heroDescription:
        "JUHUU Digital Twins lets operators represent every physical asset—from e-scooters and locker bays to full-scale infrastructure—as a live, configurable digital twin that can be controlled in real time and enriched with custom logic.",
      emailPlaceholder: "Email address",
      getStarted: "Get Started",
      statusLoading: "Please wait...",
      statusSent: "Sent successfully!",
      statusError: "Error, click to try again.",
      bullets: {
        registry: "Device registry & control",
        automation: "Step-chain automation",
        edge: "Edge execution",
      },
      dashboardSample: {
        title: "Digital Twin Dashboard",
        subtitle: "Real-time device control",
        batteryLevel: "Battery Level",
        doorStatus: "Door Status",
        unlockDevice: "Unlock Device",
      },
      section: {
        title: "Device-management hub of the JUHUU Platform",
        slogan: "Live, configurable digital twins for every physical asset",
        description:
          "JUHUU Digital Twins lets operators represent every physical asset—from e-scooters and locker bays to full-scale infrastructure—as a live, configurable digital twin that can be controlled in real time and enriched with custom logic.",
        primaryCta: "Start Managing Devices",
        secondaryCta: "View Documentation",
        badge: "CORE PLATFORM",
        logoAlt: "JUHUU Digital Twins",
        imageAlt:
          "JUHUU Digital Twins Dashboard showing device management interface",
        features: {
          registryTitle: "Device registry & control",
          registryDesc:
            "Creates a unique digital record for each piece of hardware, including custom fields and commands. Centralises fleet oversight and makes remote actions one click away.",
          automationTitle: "Step-chain automation",
          automationDesc:
            "Drag-and-drop 'step chains' trigger actions when events occur. Runs business logic automatically, on the server, in the JUHUU App, or directly on JUHUU Hardware.",
          uiLayoutsTitle: "Device-specific UI layouts",
          uiLayoutsDesc:
            "Visual editor builds on-screen buttons and controls tailored to each device. Gives end users an intuitive interface without writing native code.",
          parameterTitle: "Parameter management",
          parameterDesc:
            "Platform-wide parameters store live states—door status, tyre pressure, and more—while maintaining a full history. Enables monitoring, dashboards and downstream analytics.",
          edgeExecutionTitle: "Edge execution",
          edgeExecutionDesc:
            "Step-chain logic can be downloaded to registered JUHUU Hardware kits and executed locally via MQTT, HTTP or RS-485—even offline.",
        },
        stats: {
          deviceTypes: "Device Types",
          parametersTracked: "Parameters Tracked",
          automationRules: "Automation Rules",
        },
      },
      lifecycle: {
        title: "Device Lifecycle Management",
        description:
          "Follow the complete journey from device registration to intelligent analytics with JUHUU Digital Twins",
        play: "Play",
        pause: "Pause",
        reset: "Reset",
        step: "Step",
        keyActions: "Key Actions in this Step:",
        steps: [
          {
            title: "On-board",
            description: "Register new hardware in JUHUU Dashboard",
            details:
              "Create digital records for your physical devices with custom data fields and command definitions.",
            actions: [
              "Register device in Dashboard › Digital Twins",
              "Assign unique identifiers and metadata",
              "Define custom fields and permissible commands",
            ],
          },
          {
            title: "Configure",
            description:
              "Design UI layouts and step chains with the visual builder",
            details:
              "Use drag-and-drop tools to create custom interfaces and automation rules for your devices.",
            actions: [
              "Design device-specific UI layouts",
              "Create step chains with visual builder",
              "Define parameters and data types",
              "Set up triggers and automation rules",
            ],
          },
          {
            title: "Deploy",
            description: "Publish the twin; logic syncs automatically",
            details:
              "Deploy your digital twin configuration across cloud, app, and edge devices seamlessly.",
            actions: [
              "Publish digital twin configuration",
              "Sync logic to cloud infrastructure",
              "Deploy to JUHUU App interfaces",
              "Push to compatible edge devices",
            ],
          },
          {
            title: "Operate",
            description:
              "Live parameters update continuously; users interact through custom UI",
            details:
              "Monitor real-time device status and enable user interactions through your custom interfaces.",
            actions: [
              "Monitor live parameter updates",
              "Enable user interactions via custom UI",
              "Execute step chains automatically",
              "Handle real-time device commands",
            ],
          },
          {
            title: "Analyse",
            description:
              "Historical parameter data streams into AI Anomaly Detection",
            details:
              "Leverage historical data for predictive insights and proactive device maintenance.",
            actions: [
              "Collect historical parameter data",
              "Stream data to AI Anomaly Detection",
              "Generate predictive insights",
              "Set up proactive alerting",
            ],
          },
        ],
      },
      parameters: {
        title: "Parameter Management",
        description:
          "Platform-wide parameters store live states while maintaining full history for monitoring, dashboards and downstream analytics",
        supportedTypes: "Supported Data Types",
        exampleUsage: "Example Usage:",
        featuresTitle: "Key Features",
        viewDashboard: "View Full Dashboard",
        dashboardTitle: "Live Parameter Dashboard",
        dashboardDescription:
          "Real-time monitoring of device parameters across your entire fleet",
        cards: {
          batteryLevel: "Battery Level",
          doorStatus: "Door Status",
          temperature: "Temperature",
          operationMode: "Operation Mode",
          lastUpdated: "Last updated: {{time}}",
        },
        types: {
          string: {
            name: "String",
            description:
              "Text-based parameters for status messages, device names, and descriptions",
            example: "device_status = 'operational'",
          },
          float: {
            name: "Number",
            description:
              "Decimal number parameters for precise measurements and calculations",
            example: "tire_pressure = 2.3",
          },
          boolean: {
            name: "Boolean",
            description:
              "True/false parameters for switches, locks, and binary states",
            example: "door_locked = true",
          },
          enum: {
            name: "Enum",
            description:
              "Predefined list parameters for modes, states, and categories",
            example: "mode = 'eco' | 'normal' | 'sport'",
          },
        },
        features: {
          visibility: {
            title: "Read/Write Visibility",
            description:
              "Parameters are accessible in step chains, UI layouts, and HTTP hooks",
            benefits: [
              "Real-time data access across all platform components",
              "Seamless integration with automation logic",
              "API endpoints for external system integration",
            ],
          },
          history: {
            title: "Automatic History",
            description:
              "Time-series storage for every parameter change with full audit trail",
            benefits: [
              "Complete historical data retention",
              "Trend analysis and pattern recognition",
              "Compliance and audit requirements",
            ],
          },
          analytics: {
            title: "Analytics Integration",
            description:
              "Direct feed into AI Anomaly Detection dashboards for insights",
            benefits: [
              "Predictive maintenance capabilities",
              "Anomaly detection and alerting",
              "Performance optimization insights",
            ],
          },
        },
      },
      quickStart: {
        title: "Quick Start Guide",
        description:
          "Get your first digital twin up and running in under 15 minutes with our step-by-step guide",
        setupSteps: "Setup Steps",
        progress: "Progress",
        of: "of {{total}}",
        stepLabel: "Step {{step}}",
        previous: "Previous Step",
        next: "Next Step",
        complete: "Complete Setup",
        whatToDo: "What to do:",
        proTips: "Pro Tips:",
        steps: [
          {
            title: "Add Device",
            description:
              "Register your first device in the Digital Twins dashboard",
            action:
              "Navigate to Dashboard › Digital Twins and click 'Add New Device'",
            tips: [
              "Choose a descriptive device name for easy identification",
              "Select the appropriate device category (e-scooter, locker, etc.)",
              "Add location information for better fleet management",
            ],
          },
          {
            title: "Define Parameters",
            description:
              "Set up the data fields and commands your device will use",
            action:
              "Define parameters (battery_level, door_status, etc.) and permissible commands (unlock, lock, etc.)",
            tips: [
              "Start with essential parameters like status and battery level",
              "Use clear, descriptive parameter names",
              "Set appropriate data types for each parameter",
            ],
          },
          {
            title: "Create UI Layout",
            description: "Design the user interface for device interaction",
            action:
              "Use the visual editor to create buttons, toggles, and status indicators for your device",
            tips: [
              "Keep the interface simple and intuitive",
              "Group related controls together",
              "Use conditional visibility for advanced features",
            ],
          },
          {
            title: "Build Step Chain",
            description: "Create automation rules for device behavior",
            action:
              "Create at least one step chain to define how your device responds to events",
            tips: [
              "Start with simple trigger-action pairs",
              "Test each step chain thoroughly",
              "Consider edge cases and error handling",
            ],
          },
          {
            title: "Publish & Monitor",
            description: "Deploy your digital twin and start monitoring",
            action:
              "Attach compatible JUHUU Hardware (optional), publish your configuration, and monitor live data",
            tips: [
              "Monitor the first few interactions closely",
              "Refine your step chains based on real usage",
              "Use the analytics to optimize performance",
            ],
          },
        ],
      },
    },
    dashboard: {
      page: {
        heroBadge: "JUHUU Dashboard · For operators",
        heroTitleLine1: "Run your whole",
        heroTitleLine2Prefix: "business from",
        heroTitleHighlight: "one place",
        heroDescription:
          "As an operator you control rentals, locations, products, tariffs and billing from a single dashboard — with a real-time view of everything that's happening.",
        ctaBookDemo: "Book a demo",
        ctaLogin: "Log in",
        mockTitle: "Dashboard",
        mockWelcome: "Welcome back! Here's what's happening in your business.",
        navHome: "Home",
        navQuickActions: "Quick Actions",
        navBikeBoxes: "BikeBoxes",
        navCalendar: "Calendar",
        navTransactions: "Transactions",
        navMap: "Map",
        navOperators: "Operators",
        navBooking: "Booking",
        navBilling: "Billing",
        navSupport: "Support",
        kpis: [
          { label: "Revenue (7 days)", value: "3.000 €", delta: "▲ 69,4 %" },
          { label: "Rentals (7 days)", value: "12", delta: "▲ 33,3 %" },
          { label: "Active devices", value: "318", delta: "6 offline" },
          { label: "Utilization", value: "74 %", delta: "avg 7 days" },
        ],
        incidentsTitle: "Open incidents",
        incidentsOverview: "Overview",
        incidentAwaiting: "Awaiting confirmation",
        incidentLow: "Low",
        incidents: [
          { name: "Fahrradbox 3 | E-MTB Hardtail", date: "30.05.2026, 10:40" },
          { name: "Fahrradbox 2 | E-MTB Hardtail", date: "30.05.2026, 10:37" },
          { name: "Fahrradbox 1 | E-MTB Hardtail", date: "28.04.2026, 08:09" },
        ],
        capKicker: "Capabilities",
        capTitleLine1: "One control center.",
        capTitleLine2: "Everything in hand.",
        capLead:
          "Every module you need as an operator, in one interface, with no tool switching.",
        caps: [
          {
            title: "Manage rentals",
            description:
              "Create, export and track bookings and reservations as a timeline or table.",
          },
          {
            title: "Device management",
            description:
              "Track status & history and control hardware remotely — open, lock, reset.",
          },
          {
            title: "Locations, products & QR codes",
            description:
              "Maintain locations and products, issue QR codes and see everything on the map.",
          },
          {
            title: "Tariffs & pay-as-you-go",
            description:
              "Define prices by duration and maximum — from per-minute to annual.",
          },
          {
            title: "Billing & accounting areas",
            description:
              "Review transactions, automate invoices and assign revenue cleanly.",
          },
          {
            title: "Benefit cards & discounts",
            description:
              "Issue discount codes and reduced tariffs and connect external systems like transit.",
          },
          {
            title: "Manage customers",
            description:
              "Search, view and manage customer accounts — including verification status.",
          },
          {
            title: "Monitor incidents",
            description:
              "See open incidents by status and severity and resolve them at once.",
          },
        ],
        bookingKicker: "Booking",
        bookingTitleLine1: "Every rental",
        bookingTitleLine2: "at a glance.",
        bookingDescription:
          "Manage all rental sessions and reservations for your sites — as a timeline or table, filtered by location, colour-coded by status.",
        bookingBullets: [
          "Status: reservation, active, upcoming, pending, completed",
          "Create new rentals manually",
          "Export with one click",
        ],
        mockRentals: "Rentals",
        mockRentalBtn: "Rental",
        chipTimeline: "Timeline",
        chipTable: "Table",
        chipAllLocations: "All locations ▾",
        legendActive: "Active",
        legendUpcoming: "Upcoming",
        legendPending: "Pending",
        timelineLocation: "LOCATION",
        devicesKicker: "Device management",
        devicesTitleLine1: "Your hardware,",
        devicesTitleLine2: "controlled remotely.",
        devicesDescription:
          "Track the status of every device in real time, review its full history and control the hardware right from the dashboard — everything you need to manage your devices.",
        devicesBullets: [
          "Online/offline status & device history",
          "Remote control: open, lock, reset",
          "Register & configure IoT devices",
        ],
        mockDevices: "Devices",
        mockCreateDevice: "Create device",
        mockDevicesLead:
          "Monitor status, view history and control devices remotely.",
        tableName: "Name",
        tableStatus: "Status",
        tableAction: "Action",
        statusOnline: "Online",
        statusOffline: "Offline",
        openBox: "Open box",
        tariffsKicker: "Billing",
        tariffsTitleLine1: "Tariffs you",
        tariffsTitleLine2: "define yourself.",
        tariffsDescription:
          "Define prices by duration and maximum amount — from a pay-as-you-go per-minute tariff to an annual rental. Billing then runs fully automatically.",
        tariffsBullets: [
          "Pay-as-you-go, daily, monthly & annual tariffs",
          "Automatic invoices & transactions",
          "Assignment to accounting areas",
        ],
        mockTariffs: "Tariffs",
        mockCreate: "Create",
        mockTariffsLead: "Set prices for the services you offer.",
        tableDuration: "Duration",
        tableMaxAmount: "Max amount",
        tariffRows: [
          { name: "Pay-as-you-go", duration: "per minute", amount: "0,15 €/min" },
          { name: "Daily rent", duration: "5 min", amount: "25,00 €" },
          { name: "2 Day Rent", duration: "1 day 2 h", amount: "48,00 €" },
          { name: "Annual rent", duration: "365 days", amount: "180,00 €" },
        ],
        customersKicker: "Customers & benefit cards",
        customersTitleLine1: "Your customers.",
        customersTitleLine2: "Your conditions.",
        customersDescription:
          "Manage all customer accounts in one place — and issue benefit cards with discounts or reduced tariffs. This also lets you connect external systems such as public-transport providers and map your own conditions.",
        customersBullets: [
          "Search, view & manage customer accounts",
          "Discount codes & reduced tariffs via benefit cards",
          "Connect external systems like public-transport providers",
        ],
        mockCustomers: "Customers",
        mockUserBtn: "User",
        mockSearchPlaceholder: "Search customers…",
        mockSearchBtn: "Search",
        tableEmail: "E-Mail",
        tableVerified: "Verified",
        customKicker: "Tailored",
        customTitleLine1: "Custom dashboards",
        customTitleLine2: "for every use case.",
        customDescription:
          "We set up custom dashboards that show only what matters, tailored to your use case.",
        customBullets: [
          "Role- & team-specific views",
          "Only the relevant modules — no clutter",
          "Optimised by us for your use case",
        ],
        mockSupportView: "Support view",
        mockCustomBadge: "Custom",
        chipSupport: "Support",
        chipAccounting: "Accounting",
        chipOperations: "Operations",
        customNote: "Only 3 modules visible — reduced to the support use case.",
        customTiles: [
          { label: "Rentals", value: "12", sub: "today" },
          { label: "Customers", value: "1.284", sub: "total" },
          { label: "Devices", value: "318", sub: "online" },
        ],
        recentRentals: "Recent rentals",
        recentRows: [
          { name: "Mara Weiß · BikeBox 04", status: "active" },
          { name: "Tobias Frank · Box 01", status: "pending" },
        ],
        stats: [
          { value: "90%", label: "less admin effort" },
          { value: "24/7", label: "real-time overview" },
          { value: "1", label: "dashboard for everything" },
        ],
        trustedBy: "Trusted by",
        finalCtaTitle: "Get more out of your business.",
        finalCtaDescription:
          "See how operators work with the JUHUU dashboard — or get a personal tour of everything.",
        finalCtaButton: "Book a demo",
      },
      heroTitle: "Enterprise Dashboard",
      heroHighlight: "Analytics & Control.",
      heroDescription:
        "JUHUU's comprehensive dashboard gives you complete control over your digital ecosystem. Monitor performance, manage devices, track financials, and optimize operations—all from a single, intuitive interface.",
      emailPlaceholder: "Email address",
      getStarted: "Get started",
      realTimeMetrics: "Real-time metrics",
      customizableViews: "Customizable views",
      aiPoweredInsights: "AI-powered insights",
      billing: {
        tag: "JUHUU Billing Engine",
        title: "Powerful Financial Management",
        description:
          "Our comprehensive billing system provides complete control over your financial operations with flexible pricing models, automated invoicing, and detailed reporting.",
        overviewTitle: "Comprehensive Financial Overview",
        overviewDescription:
          "Get a complete picture of your financial performance with detailed insights into revenue streams, payment status, and financial trends.",
        revenueAnalyticsTitle: "Revenue Analytics",
        revenueAnalyticsDesc:
          "Track all revenue streams and identify growth opportunities with detailed financial reports.",
        paymentTrackingTitle: "Payment Tracking",
        paymentTrackingDesc:
          "Monitor payment status, invoices, and transaction history in real-time.",
        financialReportingTitle: "Financial Reporting",
        financialReportingDesc:
          "Generate custom reports and export financial data for accounting and analysis.",
        rentalsTitle: "Rentals and Reservations Management",
        rentalsDescription:
          "Monitor all your active rentals and upcoming reservations in real-time, with detailed financial information for each transaction.",
        activeRentalTitle: "Active Rental Tracking",
        activeRentalDesc:
          "View all active rentals, their status, and associated revenue in a single dashboard.",
        reservationManagementTitle: "Reservation Management",
        reservationManagementDesc:
          "Manage upcoming reservations, handle changes, and optimize resource allocation.",
        usageStatisticsTitle: "Usage Statistics",
        usageStatisticsDesc:
          "Analyze usage patterns to optimize inventory and improve customer experience.",
        tariffTitle: "Flexible Tariff Management",
        tariffDescription:
          "Create and manage flexible pricing models to suit your business needs, from time-based billing to subscription plans and dynamic pricing.",
        customPricingTitle: "Custom Pricing Models",
        customPricingDesc:
          "Design and implement pricing structures tailored to your specific business requirements.",
        subscriptionManagementTitle: "Subscription Management",
        subscriptionManagementDesc:
          "Set up and manage recurring billing plans with automated invoicing and payment processing.",
        locationSpecificTitle: "Location-Specific Tariffs",
        locationSpecificDesc:
          "Assign specific tariffs to each location and device individually for maximum flexibility.",
        summary:
          "The JUHUU Billing Engine provides all the tools you need to manage your financial operations efficiently and effectively.",
        learnMore: "Learn More About Billing Engine",
      },
      management: {
        tag: "Management Dashboard",
        title: "Powerful Management Tools",
        description:
          "Take control of your business with our comprehensive management dashboard, providing real-time insights and powerful tools.",
        realTimeAnalyticsTitle: "Real-time Analytics",
        realTimeAnalyticsDesc:
          "Monitor performance metrics, track usage patterns, and identify growth opportunities with comprehensive analytics dashboards.",
        financialManagementTitle: "Financial Management",
        financialManagementDesc:
          "Streamline invoicing, track revenue streams, and manage payouts across multiple business units or countries.",
        monthlyRevenue: "Monthly Revenue",
        activeUsers: "Active Users",
      },
      device: {
        statusTitle: "Device Status",
        onlineCount: "{{count}} Online",
        offlineCount: "{{count}} Offline",
        addDevice: "Add Device",
        table: {
          device: "Device",
          status: "Status",
          lastUsed: "Last Used",
          battery: "Battery",
          location: "Location",
        },
        online: "Online",
        offline: "Offline",
        oneHourAgo: "1 hour ago",
        tenMinutesAgo: "10 minutes ago",
        thirtyMinutesAgo: "30 minutes ago",
        fiveMinutesAgo: "5 minutes ago",
        acPower: "AC Power",
        locationBerlin: "Berlin Central",
        locationMunich: "Munich Station",
        locationHamburg: "Hamburg Harbor",
        locationFrankfurt: "Frankfurt City",
        sectionTag: "Device Management",
        sectionTitle: "Streamline device management and customize interfaces",
        sectionDescription:
          "Operators can access key device parameters, create custom interfaces, and program devices using a drag-and-drop interface. This section also enables managing SIM cards and overseeing the connections of each device, ensuring smooth operations and full control over the system.",
        simManagementTitle: "SIM Management",
        simManagementDesc:
          "Manage SIM cards across your devices with automated provisioning and monitoring.",
        templatesTitle: "Device Templates",
        templatesDesc:
          "Create custom interfaces and edit code directly with our intuitive template system.",
        digitalTwinsTitle: "Digital Twins",
        digitalTwinsDesc:
          "Get a realtime overview of your devices with digital replicas that mirror physical status.",
        connectorsTitle: "Connectors",
        connectorsDesc:
          "Manage connectivity of your infrastructure with our robust connection management tools.",
      },
      docs: {
        tag: "Knowledge Management",
        title: "JUHUU Docs & Buddy",
        description:
          "Create, manage, and distribute knowledge across your organization and to your customers with our intelligent documentation system and AI-powered assistant.",
        docsTitle: "JUHUU Docs",
        docsDescription:
          "Create and manage comprehensive documentation for your products, services, and FAQs directly from your dashboard. Our intelligent system automatically translates content into multiple languages and makes it accessible to your customers and the JUHUU Buddy.",
        editorTitle: "Documentation Editor",
        multilingualTitle: "Multilingual Support",
        multilingualDesc:
          "AI-powered translation automatically converts your documentation into multiple languages.",
        versionControlTitle: "Version Control",
        versionControlDesc:
          "Track changes, manage revisions, and roll back to previous versions when needed.",
        knowledgeIntegrationTitle: "Knowledge Base Integration",
        knowledgeIntegrationDesc:
          "Seamlessly connect your documentation to JUHUU Buddy for intelligent customer support.",
        buddyTitle: "JUHUU Buddy",
        buddyDescription:
          "Your AI-powered assistant that leverages your documentation to provide instant, accurate support to your customers. JUHUU Buddy learns from your knowledge base and continuously improves its responses based on customer interactions.",
        buddyConfigTitle: "Buddy Configuration",
        buddyKnowledgeTitle: "Knowledge Integration",
        buddyKnowledgeDesc:
          "Train your Buddy with your specific documentation to provide accurate, contextual responses.",
        buddyContinuousTitle: "Continuous Learning",
        buddyContinuousDesc:
          "Buddy improves over time by learning from customer interactions and feedback.",
        buddyMultilingualTitle: "Multilingual Support",
        buddyMultilingualDesc:
          "Communicate with customers in their preferred language with real-time translation.",
        ctaText:
          "Empower your team and customers with intelligent documentation and AI-powered support that continuously improves over time.",
        exploreBuddy: "Explore JUHUU Buddy",
        exploreDocs: "Explore JUHUU Docs",
      },
    },
    designYourOwn: {
      heroTitle: "Design Your Own",
      heroHighlight: "Use Case",
      heroDescription:
        "With our modular building block system, digitize any idea in just days — from bike and scooter sharing to smart parking platforms. No months of custom development needed.",
      emailPlaceholder: "Email address",
      getStarted: "Get started",
      badges: {
        fastDeployment: "Fast deployment",
        modularDesign: "Modular design",
        aiEnhanced: "AI-enhanced",
      },
      cardTitles: {
        modularDesign: "Modular Design",
        rapidIntegration: "Rapid Integration",
        timeToMarket: "Time-to-Market",
        provenSolution: "Proven Solution",
        fasterLaunch: "Up to 70% faster market launch",
      },
      introTitle: "Your Custom Use Case — Implemented in Record Time",
      introDescription:
        "JUHUU offers you the flexibility to design your own mobility solution without starting from scratch. Leverage our proven modules and save valuable development time with our plug-and-play integration.",
      features: {
        modularBlocksTitle: "Modular Building Blocks",
        modularBlocksDesc:
          "Choose exactly the functions you need. From user management to payment processing to IoT integration — all with simple API connections.",
        seamlessIntegrationTitle: "Seamless Integration",
        seamlessIntegrationDesc:
          "Thanks to standardized APIs and SDKs, you can integrate your hardware and systems in minimal time with our one-click connectors.",
        whiteLabelTitle: "White-Label Solution",
        whiteLabelDesc:
          "Fully customize the appearance to match your brand — from the app to customer support interfaces with our intuitive design system.",
      },
      integration: {
        title: "Integration Made Simple",
        description:
          "Our platform is designed for seamless integration with your existing systems and hardware. Connect in minutes, not months.",
        list: {
          oneClickTitle: "One-Click Device Onboarding",
          oneClickDesc: "Add new devices to your fleet with a simple scan",
          preBuiltTitle: "Pre-built API Connectors",
          preBuiltDesc: "Ready-made integrations for popular services",
          noCodeTitle: "No-Code Configuration",
          noCodeDesc: "Set up complex workflows without programming",
        },
        statusTitle: "Integration Status",
        statusDesc: "All systems connected",
        active: "Active",
        items: {
          paymentGateway: "Payment Gateway",
          mobileApp: "Mobile App",
          securityModule: "Security Module",
          userManagement: "User Management",
          connected: "Connected",
        },
        addButton: "Add New Integration",
      },
      process: {
        title: "Your Custom Use Case in 4 Simple Steps",
        step1Title: "Define Idea & Goals",
        step1Desc:
          "Conduct interviews with your target groups, document pain points, and formulate your use case statement. Our team will help you refine your requirements.",
        step1Tags: [
          "Requirements Analysis",
          "Target Group Definition",
          "Market Analysis",
        ],
        step2Title: "Select Modules",
        step2Desc:
          "Choose exactly the components you need from our modular technology toolkit. From user apps to operator dashboards to IoT integration and payment processing.",
        step2Tags: [
          "Module Selection",
          "Technical Specification",
          "Architecture Planning",
        ],
        step3Title: "Implementation",
        step3Desc:
          "Integrate hardware, configure the dashboard, and customize the frontend branding. Our expert team will guide you through the entire process with our one-click integration tools.",
        step3Tags: [
          "Hardware Integration",
          "Branding",
          "Configuration",
          "Testing",
        ],
        step4Title: "Pilot & Rollout",
        step4Desc:
          "Start with a small fleet, gather feedback, and then scale your rollout. Our platform grows with your requirements and adapts to your changing needs.",
        step4Tags: [
          "Pilot Project",
          "Feedback Analysis",
          "Scaling",
          "Continuous Optimization",
        ],
      },
      ecosystem: {
        title: "JUHUU Product Ecosystem",
        description:
          "Our comprehensive suite of products works together seamlessly to power your custom use case",
        coreTitle: "Core Infrastructure",
        core: {
          digitalTwinsTitle: "Digital Twins",
          digitalTwinsDesc: "Virtual replications of all your physical devices",
          identityTitle: "Identity",
          identityDesc: "Secure authentication and user management",
          specialDevTitle: "Special Development",
          specialDevDesc: "Custom integration services for complex use cases",
          strataTitle: "Strata",
          strataDesc: "Self-service integration platform",
          comingSoon: "Coming soon",
        },
        uiTitle: "User Interfaces",
        ui: {
          appTitle: "App",
          appDesc: "Native mobile applications for iOS and Android",
          webAppTitle: "WebApp",
          webAppDesc: "Browser-based booking and management interface",
          mapTitle: "Map",
          mapDesc: "Interactive location-based services and visualization",
          whitelabelTitle: "Whitelabel",
          whitelabelDesc: "Fully branded custom applications",
        },
        opsTitle: "Operations & Business",
        ops: {
          dashboardTitle: "Dashboard",
          dashboardDesc: "Comprehensive management and analytics console",
          billingTitle: "Billing",
          billingDesc: "Payment processing, invoicing and financial management",
          marketplaceTitle: "Marketplace",
          marketplaceDesc:
            "Discover and integrate certified hardware and services",
          buddyTitle: "Buddy",
          buddyDesc: "AI-powered support and assistance",
        },
      },
      productsTogetherTitle: "How Our Products Work Together",
      todayTitle: "Today: Special Development",
      todayDesc:
        "Currently, we work directly with customers to implement their custom use cases through our Special Development service. Our team of experts handles the integration of your systems with our platform.",
      todayBenefitsTitle: "Benefits:",
      todayBenefits: [
        "Fully customized solutions",
        "Expert guidance throughout the process",
        "Rapid implementation with our experienced team",
      ],
      futureTitle: "Future: Strata Platform",
      futureDesc:
        "Soon, our Strata platform will enable you to implement your own use cases without our direct involvement. You'll be able to choose from no-code, low-code, or expert integration paths based on your needs.",
      futureBenefitsTitle: "Benefits:",
      futureBenefits: [
        "Self-service integration",
        "No-code, low-code, and expert options",
        "Complete control over your implementation",
      ],
      uspTitle: "Why Choose JUHUU",
      usps: {
        speedTitle: "Speed & Scalability",
        speed1:
          "Plug-and-Play Onboarding: Deploy certified devices in days via No-Code, Low-Code and Expert Integration paths",
        speed2:
          "Modular Growth Blocks: Each new product rides on the same foundation, ensuring zero incremental complexity as you expand",
        flexibilityTitle: "Flexibility & Control",
        flexibility1:
          "Three Integration Tiers: Choose your effort level—turnkey for minimal lift, visual configurators for quick tweaks, or deep APIs for custom logic",
        flexibility2:
          "White-Label Branding: Operate within the universal JUHUU App or spin up a fully private Whitelabel shell",
        growthTitle: "Growth & Monetization",
        growth1:
          "Operator Incentives: Revenue Share (15%) and User Referral programs turn partners into marketers",
        growth2:
          "High-Margin Add-Ons: Plus Subscriptions, Affiliate Links and Integrated Bookables unlock new revenue streams",
        efficiencyTitle: "Operational Efficiency",
        efficiency1:
          "End-to-End Flow: From device data through Billing Engine, Identity and Dashboard, every touchpoint shares a unified data model",
        efficiency2:
          "Predictive Maintenance: Anomaly Detection flags failures early, cutting unplanned downtime by up to 30%",
      },
      components: {
        appDescription: {
          heading: "One Platform, Multiple Solutions",
          description:
            "JUHUU connects users with service providers through a seamless, integrated platform for mobility, infrastructure, and utilities.",
          forUsers: {
            title: "For Users",
            description:
              "Find, book, and use services from different providers all in one place. JUHUU brings together a wide range of mobility, infrastructure, and utility services in a single, easy-to-use app.",
            features: [
              {
                title: "Service Discovery",
                description: "Find nearby services with real-time availability",
              },
              {
                title: "Easy Payments",
                description: "Book and pay seamlessly within the app",
              },
              {
                title: "Unified Access",
                description:
                  "Access services from multiple providers with one account",
              },
              {
                title: "Global Coverage",
                description:
                  "Use the same app across multiple cities and countries",
              },
            ],
          },
          forProviders: {
            title: "For Providers",
            description:
              "Reach more customers and streamline your operations. JUHUU gives service providers the tools they need to offer their products and services to a wider audience.",
            features: [
              {
                title: "Large User Base",
                description: "Access to a growing community of active users",
              },
              {
                title: "Sales Channel",
                description:
                  "Direct distribution channel for all your services",
              },
              {
                title: "Whitelabeling",
                description: "Corporate identity integration within the app",
              },
              {
                title: "JUHUU Services",
                description:
                  "Direct access to deeply integrated JUHUU services",
              },
            ],
          },
          cta: "Get Started with JUHUU",
        },
      },
    },
    bikeSharing: {
      heroTitle: "Bike Sharing Use Case",
      backToOverview: "\u2190 Back to overview",
      intro:
        "Launch and manage a complete bike sharing service with our end-to-end solution. JUHUU provides everything you need: user apps, operator dashboards and payment processing.",
      underConstructionTitle: "This page is under construction",
      underConstructionText:
        "Detailed information about the bike sharing use case will be available soon.",
    },
    bikeParking: {
      heroTitle: "Smart Bike Parking",
    },
    webApp: {
      heroTitle: "Digital Ecosystems",
      heroHighlight: "In your Browser",
      components: {
        showCase: {
          features: {
            billing: {
              title: "Billing",
              subtitle: "Integrated payment system for seamless transactions",
              description:
                "The Billing module within the JUHUU App enables users to make payments and select tariffs effortlessly. Operators benefit from a complete payment infrastructure that handles subscriptions, one-time payments, and recurring billing without additional development. This integrated solution reduces administrative overhead for operators while providing users with a smooth payment experience directly in the app.",
              bullets: [
                "Reduce operational costs with automated payment processing",
                "Increase revenue with flexible pricing models for operators",
                "Gain insights through comprehensive financial analytics",
              ],
            },
            identity: {
              title: "Identity",
              subtitle: "Secure authentication and verification system",
              description:
                "The Identity module in the JUHUU App provides users with secure login options while giving operators flexible verification capabilities. Operators can request ID verification when needed for their services, enhancing security and compliance. This fully integrated identity management system simplifies user onboarding while protecting both operators and users from fraud and unauthorized access.",
              bullets: [
                "Reduce fraud and unauthorized access for operator services",
                "Simplify user verification and compliance requirements",
                "Enhance customer trust with enterprise-grade security",
              ],
            },
            map: {
              title: "Map",
              subtitle: "Comprehensive product and service discovery",
              description:
                "The Map feature in the JUHUU App displays all products and services from every provider in one unified interface. This gives users a complete overview of available options in their vicinity, increasing visibility for operators. By integrating with the Map module, operators gain immediate exposure to users searching for services like theirs, without needing to build their own discovery platform.",
              bullets: [
                "Increase visibility for operators by showing services to nearby users",
                "Expand customer reach without building a separate discovery platform",
                "Benefit from JUHUU's location-based user acquisition",
              ],
            },
            buddy: {
              title: "JUHUU Buddy",
              subtitle: "Multilingual AI assistant for users and operators",
              description:
                "JUHUU Buddy is an integrated AI assistant available directly in the JUHUU App, capable of answering questions about both the app itself and the services offered by operators. Available in 14 languages, it provides 24/7 support without requiring operators to build their own support infrastructure. This shared resource benefits all operators on the platform while enhancing the user experience.",
              bullets: [
                "Reduce customer support costs by up to 60% for operators",
                "Scale operations without proportionally increasing support staff",
                "Improve customer satisfaction with instant, multilingual assistance",
              ],
            },
            integratedBookables: {
              title: "Integrated Bookables",
              subtitle: "Pre-integrated services that enhance user experience",
              description:
                "JUHUU has already integrated key services like charging stations (via Hubject) at our own cost, making them directly bookable within the app. These Integrated Bookables make the JUHUU App more attractive to users while providing operators with a richer ecosystem. By joining the platform, operators benefit from these pre-existing integrations and the increased user base they attract.",
              bullets: [
                "Leverage pre-existing integrations like Hubject charging stations",
                "Benefit from JUHUU's investment in third-party service connections",
                "Join an ecosystem with established user demand for integrated services",
              ],
            },
          },
          learnMore: "Learn More",
          contactSales: "Contact Sales",
        },
        compatibility: {
          badge: "Available Everywhere",
          heading: "Access JUHUU from Any Device or Browser",
          description:
            "Our web platform is designed to work seamlessly across all modern browsers and devices, ensuring you can manage your mobility services from anywhere.",
          browserHeading: "Browser Compatibility",
          browserDescription:
            "JUHUU WebApp works flawlessly on all modern browsers, with no plugins or extensions required. Just open your browser and start using our platform immediately.",
          browsers: ["Chrome", "Safari", "Firefox", "Edge", "Opera"],
          deviceHeading: "Device Compatibility",
          deviceDescription:
            "Whether you're on a desktop computer, laptop, tablet, or smartphone, JUHUU WebApp adapts to your screen size and provides an optimal user experience.",
          devices: ["Desktop", "Laptop", "Tablet", "iPhone", "Android devices"],
          openWebApp: "Open JUHUU WebApp",
          contactSales: "Contact Sales",
          noDownloads: "No downloads or installations required",
        },
      },
      heroDescription:
        "JUHUU connects technology, infrastructure, and utilities through our powerful web platform. Access our complete ecosystem directly in your browser—no downloads required. Manage mobility, smart infrastructure, and utility services from anywhere, on any device.",
      emailPlaceholder: "Email address",
      getStarted: "Get started",
      webAppFeatures: [
        {
          title: "Browser-Based Access",
          description:
            "Access all features directly from your browser without downloading any software",
        },
        {
          title: "Secure Platform",
          description:
            "Enterprise-grade security with encrypted connections and data protection",
        },
        {
          title: "Real-Time Analytics",
          description:
            "Monitor usage and performance with comprehensive dashboards and reports",
        },
      ],
      webPlatformTag: "Web Platform",
      webPlatformTitle: "Powerful Mobility Management in Your Browser",
      webPlatformDescription:
        "Our JUHUU WebApp delivers powerful features optimized for desktop and laptop use. Manage your entire mobility ecosystem from a single browser window.",
      featureCard1Title: "Enterprise-Grade Platform",
      featureCard1Description:
        "Built for scale and reliability, our web platform handles millions of transactions with 99.9% uptime and enterprise-level security.",
      featureCard1List: [
        "High availability architecture",
        "Encrypted data storage",
        "Regular security audits",
      ],
      featureCard2Title: "Powerful Analytics",
      featureCard2Description:
        "Gain deeper insights with comprehensive analytics dashboards. Track usage patterns, monitor performance metrics, and make data-driven decisions.",
      featureCard2List: [
        "Real-time data visualization",
        "Custom reporting tools",
        "Predictive analytics",
      ],
      featureCard3Title: "Seamless Integration",
      featureCard3Description:
        "Connect with your existing systems and third-party services. Our open API architecture ensures compatibility with your technology stack.",
      featureCard3List: [
        "RESTful API architecture",
        "Webhook support",
        "Custom connectors",
      ],
      userExperienceTag: "User Experience",
      bookingTitle: "Seamless Booking Experience",
      bookingDescription:
        "From discovery to usage in just a few clicks - see how your customers can easily access your services through the JUHUU WebApp",
      featuresTag: "Features",
      featuresTitle: "Comprehensive Web Platform",
      featuresDescription:
        "Our JUHUU WebApp offers a complete suite of tools for managing mobility services, infrastructure, and utilities",
      customInterfaceTag: "Customizable Interface",
      customInterfaceTitle: "Your Brand, Our Technology",
      customInterfaceDescription:
        "JUHUU enables organizations to seamlessly integrate their own corporate identity into the web platform. Maintain your brand identity while leveraging our powerful technology.",
      becomePartner: "Become a Partner",
      partnerDescription: "Integrate your services into our growing network",
      customInterfaces: [
        {
          title: "Custom Branding",
          description:
            "Organizations can integrate their own logo, colors, and design into the web platform.",
          features: [
            "Incorporate your own logo",
            "Apply your brand colors",
            "Customize product names",
            "Consistent brand experience",
          ],
        },
        {
          title: "Tailored Pricing",
          description:
            "Flexible tariff design and product categorization according to your needs.",
          features: [
            "Custom tariff structures",
            "Special discounts and offers",
            "Time-based or usage-based billing",
            "Flexible pricing options",
          ],
        },
        {
          title: "Seamless Integration",
          description:
            "Complete integration into existing brand identities like ÖBB Bike.",
          features: [
            "Consistency with other brand channels",
            "Familiar interface for customers",
            "Customizable feature labels",
            "Your own terms of use",
          ],
        },
      ],
      liveDemoTag: "Live Demo",
      liveDemoTitle: "Explore JUHUU WebApp in Action",
      liveDemoDescription:
        "Discover available locations, operators, and services in real-time. See how easy it is to find and book mobility services across our growing network.",
      mapHeading: "Interactive Map Experience",
      mapDescription:
        "Browse through our interactive map to see all available services in your area. Explore different operators, locations, and mobility options all in one place.",
      statProviders: "Service providers",
      statBookingTime: "Average booking time",
      statAvailability: "Service availability",
      partners: "Already partnering with us",
      joinOperator: "Join us as an operator",
      openWebApp: "Open JUHUU WebApp",
      viewCustomInterface: "View custom interface",
    },
    smartCity: {
      heroTitle: "The Complete Smart City",
      heroHighlight: "Platform",
      featuredBadge: "Featured",
      heroHeadingMain: "Smart Cities",
      heroHeadingHighlight: "Reimagined",
      heroDescription:
        "Transform urban infrastructure into a unified, intelligent ecosystem. Deploy, manage, and scale smart city services with enterprise-grade reliability.",
      dashboardTitle: "Smart City Dashboard",
      serviceBikeParking: "Bike Parking",
      serviceBikeParkingCount: "1,247 active",
      serviceEvCharging: "EV Charging",
      serviceEvChargingCount: "89 stations",
      serviceSmartParking: "Smart Parking",
      serviceSmartParkingCount: "2,156 spots",
      serviceCitizenApp: "Citizen App",
      serviceCitizenAppCount: "45K users",
      unifiedPlatformTitle: "Unified Platform",
      unifiedPlatformSubtitle: "All services, one dashboard",
      usageGrowth: "Usage Growth",
      badgeEnterprise: "Enterprise Platform",
      overviewDescription:
        "JUHUU is designed to help cities unify their urban mobility and infrastructure services, reducing integration complexity and delivering seamless citizen experiences.",
      cityAdminTitle: "For City Administrators",
      cityAdminDesc:
        "Centralize all connected urban assets—bike-parking, scooter hubs, EV chargers, and parking bays—under one unified platform with real-time insights and automated citizen billing.",
      cityAdminBullet: "Real-time Management",
      innovationTeamsTitle: "For Innovation Teams",
      innovationTeamsDesc:
        "Launch new smart city services in weeks using our modular platform. From pilot to city-wide deployment with pre-built integrations for popular urban infrastructure.",
      innovationTeamsBullet: "Rapid Deployment",
      citizensTitle: "For Citizens",
      citizensDesc:
        "Access all city services through one familiar app. Book parking, unlock bikes, charge vehicles, and pay seamlessly—all while the city gains valuable utilization data.",
      citizensBullet: "Unified Experience",
      processBadge: "Implementation Process",
      processTitlePrefix: "How JUHUU",
      processTitleHighlight: "Works",
      processDescription:
        "From initial consultation to full deployment, our streamlined process ensures your smart city initiative launches successfully.",
      step1Title: "Discovery & Planning",
      step1Desc:
        "We analyze your existing infrastructure, identify integration opportunities, and create a customized roadmap for your smart city transformation.",
      step1Duration: "1-2 weeks",
      step2Title: "Pilot Implementation",
      step2Desc:
        "Start with a focused pilot program to validate the solution, gather citizen feedback, and demonstrate ROI before bigger deployment.",
      step2Duration: "3-4 weeks",
      step3Title: "Full Deployment",
      step3Desc:
        "Scale the successful pilot across your entire city with our proven deployment methodology, comprehensive training, and ongoing support.",
      step3Duration: "3-6 months",
      applicationsBadge: "Platform Benefits",
      applicationsTitlePrefix: "Why Choose",
      applicationsTitleHighlight: "JUHUU",
      applicationsDescription:
        "JUHUU delivers significant time savings and cost reduction compared to traditional development approaches.",
      feature1Title: "Unified Service Management",
      feature1ImageAlt: "Unified Service Management",
      centralizedPlatform: "Centralized Platform",
      centralizedPlatformDesc:
        "Manage all city services—parking, transit, utilities—from a single dashboard.",
      realTimeMonitoring: "Real-Time Monitoring",
      realTimeMonitoringDesc:
        "Track service utilization, identify bottlenecks, and optimize resource allocation with live data feeds.",
      feature2Title: "Seamless Citizen Experience",
      feature2ImageAlt: "Seamless Citizen Experience",
      unifiedAppAccess: "Unified App Access",
      unifiedAppAccessDesc:
        "Citizens access all services—parking, transit, payments—through a single, intuitive app.",
      personalizedServices: "Personalized Services",
      personalizedServicesDesc:
        "Tailor service offerings based on citizen preferences, location, and past behavior.",
      feature3Title: "Rapid Implementation",
      deploymentShort: "8-12 Week Deployment",
      deploymentDesc:
        "From planning to go-live in weeks, not months, with our implementation methodology.",
      prebuiltIntegrations: "Pre-built Integrations",
      prebuiltIntegrationsDesc:
        "Connect to existing infrastructure with minimal custom development required.",
      feature4Title: "Future-Proof Architecture",
      openApis: "Open APIs",
      openApisDesc:
        "Easily integrate new services and technologies as your city evolves.",
      scalableInfrastructure: "Scalable Infrastructure",
      scalableInfrastructureDesc:
        "Built to handle growing urban populations and expanding service offerings.",
      caseStudiesBadge: "Success Stories",
      trustedBy: "Trusted by",
      trustedByHighlight: "Leading Cities",
      trustedByDesc: "Real implementations across European cities",
      case1Title: "Major German City",
      case1Subtitle: "1,500+ Bike Parking Spots",
      case1Desc:
        "Centralized management of over 1,500 bike parking spots from multiple vendors under one unified JUHUU platform, providing complete visibility and control.",
      case1Quote:
        '"JUHUU has transformed how we manage our bike parking infrastructure. The unified platform gives us complete visibility across all vendors."',
      case1Author: "— Mobility Director",
      case2Title: "European Municipality",
      case2Subtitle: "800 Racks Digitized",
      case2Desc:
        "Retrofitted 800 analog bike racks with Bluetooth locks and real-time booking capabilities through JUHUU, achieving 45% higher utilization rates.",
      case2Quote:
        '"The retrofit solution was cost-effective and delivered immediate results. Citizens love the seamless booking experience."',
      case2Author: "— Transportation Manager",
    },
    pricing: {
      customInterfaces: [
        {
          title: "Custom Branding",
          description:
            "Providers can integrate their own logo, colors, and design into the app.",
          features: [
            "Incorporate your own logo",
            "Apply your brand colors",
            "Customize product names",
            "Consistent brand experience",
          ],
        },
        {
          title: "Tailored Pricing",
          description:
            "Flexible tariff design and product categorization according to your needs.",
          features: [
            "Custom tariff structures",
            "Special discounts and offers",
            "Time-based or usage-based billing",
            "Flexible pricing options",
          ],
        },
        {
          title: "Seamless Integration",
          description:
            "Complete integration into existing brand identities like ÖBB Bike.",
          features: [
            "Consistency with other brand channels",
            "Familiar interface for customers",
            "Customizable feature labels",
            "Your own terms of use",
          ],
        },
      ],
      features: [
        {
          title: "Mobility",
          description:
            "Comprehensive mobility solutions for urban transportation needs.",
          benefits: [
            "Seamless integration with public transport",
            "Real-time tracking and notifications",
            "Flexible payment options",
            "Multi-modal journey planning",
          ],
        },
        {
          title: "Infrastructure",
          description:
            "Smart infrastructure management for cities and businesses.",
          benefits: [
            "IoT-enabled monitoring systems",
            "Predictive maintenance",
            "Resource optimization",
            "Environmental impact tracking",
          ],
        },
        {
          title: "Utilities",
          description:
            "Efficient utility management solutions for modern needs.",
          benefits: [
            "Smart metering integration",
            "Usage analytics and insights",
            "Automated billing systems",
            "Consumption optimization",
          ],
        },
        {
          title: "Payments",
          description:
            "Secure and flexible payment processing for all services.",
          benefits: [
            "Multiple payment methods",
            "Subscription management",
            "Automated invoicing",
            "Revenue sharing capabilities",
          ],
        },
        {
          title: "Analytics",
          description: "Powerful analytics tools to drive business decisions.",
          benefits: [
            "Real-time dashboards",
            "Custom reporting",
            "Predictive analytics",
            "User behavior insights",
          ],
        },
        {
          title: "Integration",
          description:
            "Seamless integration with existing systems and third-party services.",
          benefits: [
            "Open API architecture",
            "Custom connectors",
            "Legacy system compatibility",
            "Plug-and-play modules",
          ],
        },
        {
          title: "Security",
          description: "Enterprise-grade security for all platform components.",
          benefits: [
            "End-to-end encryption",
            "Multi-factor authentication",
            "Regular security audits",
            "Compliance with global standards",
          ],
        },
      ],
    },
    useCases: {
      heroTitle: "Use Cases",
      heroDescription:
        "Discover how JUHUU can be used for different use cases to achieve your business goals.",
      designYourOwnTitle: "Design Your Own Use Case",
      designYourOwnDescription:
        "With our modular toolkit, you can digitize any idea within days—from bike and scooter sharing to smart parking platforms.",
      bikeParkingTitle: "Bike Parking",
      bikeParkingDescription:
        "Our comprehensive bike parking solution offers OEMs and operators a complete management system with access controls and payment processing.",
      bikeSharingTitle: "Bike Sharing",
      bikeSharingDescription:
        "Launch and manage a full bike sharing service with our end-to-end solution for user apps, operator dashboards, and payments.",
      learnMore: "Learn more →",
    },
  },
  components: {
    nav: {
      products: {
        app: "App",
        appSub: "For end users",
        dashboard: "Dashboard",
        dashboardSub: "For operators",
        locking: "Locking systems",
        lockingSub: "Hardware & access",
      },
      industries: {
        bikeParking: "Bike parking",
        bikeRental: "Bike rental",
        other: "Other use cases",
      },
      industriesLabel: "Industries",
      productLabel: "Product",
      companyLabel: "Company",
      references: "References",
      about: "About",
      bookDemo: "Book demo",
      getTheApp: "Get the app",
    },
    siteFooter: {
      tagline:
        "The all-in-one platform for the sharing economy — one app, one dashboard, smart locking systems.",
      legalLabel: "Legal",
      imprint: "Imprint",
      privacy: "Privacy",
      cookies: "Cookies",
      saasAgreement: "Global SaaS agreement",
      termsOfService: "Global terms of service",
      cookieSettings: "Cookie settings",
      rightsReserved: "All rights reserved.",
      trademarks:
        "Apple, the Apple logo, iPhone and iPad are trademarks of Apple Inc., registered in the U.S. and other countries. App Store is a service mark of Apple Inc. Google Play and the Google Play logo are trademarks of Google LLC. All other trademarks are the property of their respective owners.",
    },
    cta: {
      ready: "Ready to transform your business?",
      join: "Join hundreds of companies already using JUHUU to build, launch, and scale their mobility and infrastructure solutions.",
      launch: "Launch your solution in weeks, not months",
      scale: "Scale seamlessly as your business grows",
      pay: "Pay only for what you use with our modular approach",
      start: "Get started today",
      fullName: "Full Name",
      workEmail: "Work Email",
      company: "Company",
      message: "Message",
      namePlaceholder: "Enter your name",
      emailPlaceholder: "you@company.com",
      companyPlaceholder: "Your company name",
      messagePlaceholder: "How can we help you?",
      submit: "Contact Sales",
      wait: "Please wait...",
      sent: "Sent successfully!",
      error: "Error, click to try again.",
      terms: "Terms of Service",
      privacy: "Privacy Policy",
      consent: "By submitting this form, you agree to our",
      and: "and",
    },
    navbar: {
      logoAlt: "JUHUU Logo",
      badgeComingSoon: "Coming soon",
      buttons: {
        products: "Products",
        solutions: "Use Cases",
        developers: "Developers",
        pricing: "Pricing",
        signIn: "Sign in",
        contactSales: "Contact sales",
      },
      categories: {
        core: "Core Infrastructure & Developer Tools",
        userAccess: "User Access & Booking Interfaces",
        commerce: "Commerce & Monetization",
        operations: "Operations and Monitoring",
        customerSupport: "Customer Support",
        comingSoonTitle: "Coming Soon",
        userGroups: "User Groups",
        useCases: "Use Cases",
      },
      products: {
        digitalTwins: {
          title: "Digital Twins",
          desc: "Virtual replications",
          alt: "Digital Twins",
        },
        docs: {
          title: "Docs",
          desc: "Documentation management",
          alt: "Docs",
        },
        identity: {
          title: "Identity",
          desc: "Identity management",
          alt: "Identity",
        },
        plugIn: {
          title: "Plug In",
          desc: "Extensible integrations",
          alt: "Plug In",
        },
        specialDevelopment: {
          title: "Special Development",
          desc: "Custom development",
          alt: "Special Development",
        },
        strata: {
          title: "Strata",
          desc: "Data analysis platform",
          alt: "Strata",
        },
        app: {
          title: "App",
          desc: "Mobile applications",
          alt: "App",
        },
        map: {
          title: "Map",
          desc: "Location-based services",
          alt: "Map",
        },
        webApp: {
          title: "WebApp",
          desc: "Web application development",
          alt: "WebApp",
        },
        whitelabel: {
          title: "Whitelabel",
          desc: "White label solutions",
          alt: "Whitelabel",
        },
        billing: {
          title: "Billing",
          desc: "Invoicing",
          alt: "Billing",
        },
        marketplace: {
          title: "Marketplace",
          desc: "Platform for sellers",
          alt: "Marketplace",
        },
        plusSubscriptions: {
          title: "Plus Subscriptions",
          desc: "Premium subscriptions",
          alt: "Plus Subscriptions",
        },
        revenueShare: {
          title: "Revenue Share",
          desc: "Revenue sharing",
          alt: "Revenue Share",
        },
        anomalyDetection: {
          title: "Anomaly Detection",
          desc: "Detection of irregularities",
          alt: "Anomaly Detection",
        },
        dashboard: {
          title: "Dashboard",
          desc: "Business data visualization",
          alt: "Dashboard",
        },
        advancedSupport: {
          title: "Advanced Support",
          desc: "Advanced customer support",
          alt: "Advanced Support",
        },
        buddy: {
          title: "Buddy",
          desc: "Personal support assistant",
          alt: "Buddy",
        },
      },
      comingSoonList: {
        affiliateLinks: "Affiliate Links",
        aiRouting: "AI Routing",
        integratedBookables: "Integrated Bookables",
        plusSubscription: "Plus Subscription",
        referalLinks: "Referal Links",
        tenderService: "Tender Service",
      },
      userGroups: {
        oems: "For OEMs",
        operators: "For Operators",
        users: "For Users",
      },
      useCases: {
        designYourUseCase: "Design your Use Case",
        smartCity: "Smart City",
        bikeParking: "Bike Parking",
        bikeSharing: "Bike Sharing",
        scooterSharing: "Scooter Sharing",
      },
      developers: {
        documentation: "Documentation",
        apiReference: "API Reference",
        libraries: "Libraries",
      },
    },
    footer: {
      altLogo: "JUHUU Logo",
      social: {
        linkedin: "aedIn",
        youtube: "YouTube",
        instagram: "Instagram",
        facebook: "Facebook",
      },
      contactUs: "Contact us",
      general: "General",
      phone: "+43 660 9919 009",
      email: "office@juhuu.app",
      addressHeading: "Address for meetings & shipments:",
      address: {
        line1: "Lassallestraße 9,",
        line2: "1020 Wien,",
        line3: "Austria",
      },
      important: "Important",
      developers: "Developers",
      globalTerms: "Global Terms & Conditions",
      agreement: "Agreement",
      globalSaas: "Global SaaS Agreement",
      privacy: "Privacy",
      imprint: "Imprint",
      cookiePolicy: "Cookie Policy",
      juhuuApp: "JUHUU App",
      appStoreAlt: "Download on the App Store",
      googlePlayAlt: "Get it on Google Play",
      juhuuWebApp: "JUHUU WebApp",
      openWebApp: "Open WebApp",
      copyright:
        "© 2025 Copyright: JUHUU Bikebox GmbH. Apple, the Apple logo, iPhone, and iPad are trademarks of Apple Inc., registered in the U.S and other countries. App Store is a service mark of Apple Inc. Google Play and the Google Play logo are trademarks of Google LLC. All other trademarks are the property of their respective owners.",
      termsOfService: "Terms of Service",
      privacyPolicy: "Privacy Policy",
      cookieSettings: "Cookie Settings",
      sitemap: "Sitemap",
    },
    sendInquiry: {
      emailPlaceholder: "Email address",
      submit: "Send inquiry",
      wait: "Please wait...",
      sent: "Sent successfully!",
      error: "Error, click to try again.",
    },
    page: {
      navItems: {
        useCases: "Use Cases",
        app: "App",
        dashboard: "Dashboard",
        kit: "Digitalize Kit",
        marketplace: "Marketplace",
        pricing: "Pricing",
      },
      close: "Close",
    },
  },
  bikeParking: {
    hero: {
      forCities: "For municipalities & cities",
      description:
        "JUHUU revolutionizes bicycle parking in your municipality. Our digital platform turns existing and new parking facilities into smart, revenue‑generating assets – 70% faster to deploy and up to 80% cheaper.",
      emailPlaceholder: "Your email address",
      ctaButton: "Free consultation",
      fastImplementation: "Fast implementation",
      gdprCompliant: "GDPR compliant",
      costEfficient: "Cost efficient",
    },
    payment: {
      magazineTitle: "Abstraction Magazine",
      magazinePrice: "19 € per month",
      applePay: "Pay",
      orCard: "Or pay with card",
      emailPlaceholder: "Email",
      cardDetails: "Card details",
      cardNumber: "Number",
      expire: "MM/YY",
      cvc: "CVC",
      countryLabel: "Country/Region",
      countryUs: "United States",
      zip: "ZIP",
      pay: "Pay",
    },
    stats: {
      today: "Today",
      week: "This week",
      utilization: "Utilization",
      yesterday: "Yesterday",
      now: "Today, 14:00",
      title: "BikeParking utilization",
    },
    cityBenefits: {
      optimized: "Optimized for municipalities",
      title: "Why cities choose JUHUU",
      description:
        "Our platform provides cities and municipalities with a unified solution for modern bike parking",
      main: {
        unifiedTitle: "Unified citizen experience",
        unifiedDesc: "One app for all bike parking providers in your city",
        dataTitle: "Data-driven decisions",
        dataDesc: "Real-time data to optimize your bicycle infrastructure",
        costTitle: "Cost efficiency",
        costDesc: "Up to 80% savings compared to single solutions",
        fastTitle: "Fast implementation",
        fastDesc: "Ready in weeks instead of months",
      },
      centralLabel: "Central management",
      centralTitle: "Central management of all bike parking sites",
      central: {
        dashboardTitle: "Central dashboard",
        dashboardDesc: "Manage all providers and locations via one platform",
        appTitle: "All-in-one app",
        appDesc:
          "One app for all bicycle parking or mobility services in your city with corporate design",
        analyticsTitle: "Comprehensive analytics",
        analyticsDesc:
          "Usage statistics, occupancy and trends for better city planning",
        userTitle: "Automated user management",
        userDesc: "Self-service registration and management of users",
        billingTitle: "Automated invoicing",
        billingDesc: "Automatic creation and sending of invoices to users",
        payoutTitle: "Automated payouts",
        payoutDesc: "Regular payouts to operators and commission settlement",
      },
      realtimeOverlay: "Real-time overview",
      retrofitLabel: "Retrofit",
      retrofitTitle: "Upgrade existing facilities intelligently",
      retrofitDesc:
        "We can integrate almost all existing bicycle parking facilities – from simple stands and lockers to complex towers. Integration is done via API or by retrofitting with our electronics.",
      retrofit: {
        bluetooth: "Plug-and-play Bluetooth Euro locks",
        iot: "IoT locking systems for complex facilities",
        ready: "Ready in a few days",
      },
      retrofitOverlay: "Easy installation",
      marketplaceLink: "Discover all electronics in the marketplace",
    },
    appWebApp: {
      experienceLabel: "Seamless user experience",
      title: "Unified app & web app for all citizens",
      description:
        "Provide your citizens with an intuitive and unified experience for all bike parking facilities in your city",
      overlay: "Whitelabel capable",
      appSectionTitle: "JUHUU App",
      appSectionDesc:
        "The JUHUU app lets citizens find, book and use all bike parking facilities in your city through a single intuitive interface. Each operator gets a branded interface so users immediately know when they are using one of your locations.",
      internalWhiteTitle: "Internal whitelabeling",
      internalWhiteDesc: "Individual branding for each operator within the app",
      bluetoothTitle: "Bluetooth unlocking",
      bluetoothDesc: "Contactless unlocking of boxes and stands",
      iotTitle: "IoT integration",
      iotDesc: "Support for IoT controlled locking systems and sensors",
      externalWhiteTitle: "External whitelabeling",
      externalWhiteDesc:
        "Fully customizable to match your city corporate design",
      moreApp: "Learn more about the JUHUU App",
      exploreWeb: "Discover WebApp",
      webLabel: "Browser based",
      webTitle: "JUHUU WebApp",
      webDesc:
        "The browser-based alternative to the JUHUU App is included by default. Users can see all locations with availability and prices live, book them and open mobile-controlled locations directly from the browser.",
      noInstallTitle: "No installation needed",
      noInstallDesc: "Usable immediately via any web browser",
      responsiveTitle: "Responsive design",
      responsiveDesc: "Optimized for all devices from smartphone to desktop",
      availableVia: "Available via:",
      testWebApp: "Test WebApp",
      downloadTitle: "Download now",
    },
    dashboard: {
      controlLabel: "Central control",
      title: "Comprehensive dashboard for municipalities",
      description:
        "Keep track of all bike parking facilities in your city with our powerful dashboard",
      deviceTitle: "Device management",
      deviceDesc:
        "Comprehensive management of all bike parking devices and facilities",
      deviceBullets: [
        "Add, remove and lock devices",
        "Overview of utilization and device status",
        "Remote opening and maintenance mode",
      ],
      billingTitle: "Billing",
      billingDesc: "Fully automated billing and payment processing",
      billingBullets: [
        "Automatic invoicing to users",
        "Automatic payouts to operators",
        "Refunds, deposits and credits",
      ],
      analyticsTitle: "Data analytics",
      analyticsDesc:
        "Comprehensive statistics and reports on usage and occupancy",
      analyticsBullets: [
        "Export function (CSV, PDF, Excel)",
        "Occupancy and revenue statistics",
        "User behaviour and trends",
      ],
      tariffLabel: "Flexible tariff systems",
      tariffTitle: "Tailor-made pricing models",
      tariffDesc:
        "We offer very flexible tariff options that can be added per location. Everything from pay-as-you-go to daily, monthly and yearly tariffs is freely configurable. We also support the integration of local or national discount cards for reduced tariffs.",
      tariffCards: {
        timeTitle: "Time-based tariffs",
        timeDesc: "Pay-as-you-go, day, month, year",
        companyTitle: "Company accounts",
        companyDesc: "Quotas for employees with central billing",
        familyTitle: "Family accounts",
        familyDesc: "Shared use for family members",
        benefitTitle: "Benefit cards",
        benefitDesc: "Integration of local and national discount systems",
      },
      tariffOverlay: "Individually adjustable",
    },
    aiSupport: {
      label: "AI-assisted support",
      title: "JUHUU Buddy - your smart assistant",
      description:
        "24/7 support for citizens and administrators with our AI-powered assistant",
      chatIntro:
        "Hello! I'm JUHUU Buddy, your AI assistant for all questions about bike parking. How can I help you today?",
      chatQuestion: "I want to know how to report a defective bike stand.",
      chatAnswer:
        "You can report defective bike stands directly via the JUHUU App. Open the app, select the location and tap 'Report a problem'. Alternatively you can scan the QR code at the location and report it via the WebApp.",
      supportTitle: "24/7 AI-assisted support",
      supportDesc:
        "JUHUU Buddy is an AI chatbot trained on all your documentation. Integrated in app, web and dashboard, it answers questions, performs simple tasks and escalates to human support when needed.",
      featureEverywhereTitle: "Available everywhere",
      featureEverywhereDesc:
        "Integrated in app, web app and dashboard for seamless support",
      featureAlwaysTitle: "24/7 availability",
      featureAlwaysDesc:
        "Round-the-clock support with no waiting times for your users",
      featureTasksTitle: "Automated tasks",
      featureTasksDesc:
        "Performs simple tasks such as location search or status requests",
      featureLanguagesTitle: "Multilingual",
      featureLanguagesDesc:
        "Supports multiple languages for international visitors",
      learnMore: "Learn more about AI support",
      requestDemo: "Request demo",
      statAnswered: "of requests answered automatically",
      statAvailability: "Availability with no waiting time",
      statCost: "Reduction in support costs",
    },
    hardware: {
      ecosystemLabel: "Hardware ecosystem",
      title: "Our hardware partners",
      description:
        "JUHUU works with leading manufacturers to provide you with the best hardware selection",
      marketplaceButton: "Discover all partners in the marketplace",
      independentLabel: "Manufacturer independent",
      independentTitle: "Manufacturer-independent integration",
      independentDesc:
        "JUHUU works with hardware from all leading manufacturers. Our open platform lets you choose the best hardware for your requirements without being tied to one vendor.",
      independentBullets: [
        "Self-powered Bluetooth locks without external power",
        "Cellular connectivity for controllers and sensors",
        "Solar-powered systems",
        "Retrofit modules for existing facilities",
      ],
    },
    pricingSection: {
      heading: "PRICING",
      decorativeAlt: "Decorative background",
      heroStart: "Unlock",
      heroHighlight1: "Limitless Possibilities",
      heroMid: "with",
      heroHighlight2: "Pay-As-You-Go",
      heroEnd: "Pricing",
      description:
        "No upfront costs, no hidden fees — just transparent pricing that scales with your success. Only pay for what you use, when you use it.",
      deviceTypes: {
        iot: {
          name: "IoT Devices",
          description:
            "Devices with SIM card provided by JUHUU. These include smart locks, parking sensors, and other connected hardware that require cellular connectivity.",
        },
        online: {
          name: "Online Devices",
          description:
            "Third-party devices with connectivity not provided by JUHUU. These include partner hardware, existing infrastructure, and other connected devices that you already own.",
        },
        offline: {
          name: "Offline Devices",
          description:
            "Parking spaces without direct control. These include bicycle rooms, parking spots, and other spaces that are managed through the JUHUU platform but don't have direct electronic control.",
        },
        bluetooth: {
          name: "Bluetooth Devices",
          description:
            "Standard Bluetooth devices provided by JUHUU. These include Bluetooth locks, beacons, and other proximity-based hardware that communicate via Bluetooth technology.",
        },
      },
      deviceLicenseTitle: "Monthly Device License",
      deviceInfo: "Only pay for active devices. Volume discounts apply automatically.",
      table: {
        volume: "Volume",
        price: "Price",
        savings: "Savings",
        save: "Save {{savings}}%",
        basePrice: "Base price",
      },
      includedProducts: "Included Products:",
      modules: {
        app: "JUHUU App",
        webapp: "JUHUU Web App",
        dashboard: "JUHUU Dashboard",
        marketplace: "JUHUU Marketplace",
        map: "JUHUU Map",
        docs: "JUHUU Docs",
        billing: "JUHUU Billing Engine",
      },
      hideExplanation: "Hide Device Explanation",
      showExplanation: "How Device Pricing Works",
      explanationTitle: "How Device Pricing Works",
      explanationIntro:
        "Our pricing is based on the actual devices and spaces you have in operation:",
      explanationPoints: {
        iot: "Monthly fee for each device with SIM card provided by JUHUU (e.g., 10 bicycle boxes = 10 device fees)",
        offline:
          "For spaces without direct control (e.g., a bicycle room with 100 spaces would incur IoT device fees for each controlled lock plus Offline device fees for each parking space)",
        online:
          "For third-party devices with connectivity not provided by JUHUU (additional manufacturer-specific fees may apply)",
        bluetooth: "For standard Bluetooth devices provided by JUHUU",
      },
      volumeDiscount:
        "Volume discounts are applied based on the total number of devices in your entire fleet, not per location.",
      transactionTitle: "Transaction Commission",
      transactionInfo:
        "Commission on each booking transaction processed through the JUHUU platform. Only pay when you earn.",
      payOnlyLabel: "Pay Only For Actual Bookings",
      payOnlyText: "Commission is only charged when a successful booking occurs.",
      examples: "Examples:",
      booking: "booking",
      fee: "Fee: {{amount}}",
      feeMax: "Fee: {{amount}} (max)",
      premiumTitle: "Premium Services",
      premiumDesc:
        "Additional services that can be purchased separately to enhance your experience",
      comingSoon: "COMING SOON",
      premiumServices: {
        buddy: {
          name: "JUHUU Buddy",
          description:
            "AI-powered assistant for users and operators with natural language understanding.",
          pricing: "Pay-as-you-go",
          pricingDetails: "Token-based billing",
        },
        identity: {
          name: "Identity Verification",
          description:
            "Secure identity verification for users with document scanning and biometric checks.",
          pricing: "€2",
          pricingDetails: "/verification",
        },
        support: {
          name: "Advanced Customer Support",
          description:
            "Premium support with dedicated account manager and priority response times.",
          pricing: "€300-500",
          pricingDetails: "/operator + token overage",
        },
        maintenance: {
          name: "Predictive Maintenance",
          description:
            "AI-powered anomaly detection and predictive maintenance for your device fleet.",
          pricing: "€1",
          perDevice: "/device",
          pricingDetails: "+ token overage",
        },
      },
      calculatorTitle: "Price Calculator",
      calculatorDesc: "Estimate your monthly costs based on your specific needs",
      simulatorTitle: "JUHUU Price Simulator",
      configuration: "Your Configuration",
      deviceTypesTitle: "Device Types",
      monthlyTransactions: "Monthly Transactions",
      numberOfBookings: "Number of Bookings",
      commissionPerBooking: "10% commission per booking",
      avgBookingValue: "Average Booking Value (€)",
      minMaxFee: "Min fee: €0.50, Max fee: €5.00",
      estimatedTitle: "Estimated Monthly Cost",
      deviceLicenses: "Device Licenses",
      devicesTotal: "devices total",
      transactionCommissions: "Transaction Commissions",
      totalMonthlyCost: "Total Monthly Cost",
      estimateDisclaimer:
        "This is an estimate based on your inputs. Actual costs may vary based on usage patterns, volume discounts, and other factors. Contact our sales team for a detailed quote.",
      contactSales: "Contact Sales for Detailed Quote",
      note:
        'JUHUU reserves the right to introduce separate fees for additional products in the future. All modules not listed under "Premium Plugins/Products" are currently covered by the Device Licensing and Transaction Commissions.',
    },
  },
} as const;
